import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, MapPin, CheckCircle2, X, Plus, Camera, DollarSign, MessageSquare, Trash2, AlertTriangle, ChevronDown, ExternalLink, Copy, Upload, Loader2 } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { ServiceProvider } from './types';
import AdminLayout from './AdminLayout';
import {
  DEFAULT_VISIT_DURATION_MINUTES,
  VISIT_DURATION_OPTIONS_MINUTES,
  formatVisitDurationLabel,
  isValidVisitDurationMinutes,
} from '../../lib/visitDuration';
import {
  MAINTENANCE_CHECKLIST_TEMPLATE,
} from './constants/maintenanceVisitTemplates';
import type { MaintenanceFrequencyOption } from './constants/maintenanceVisitTemplates';
import ConfigureHomeSystemsModal from '../../components/ConfigureHomeSystemsModal';
import { ScheduleVisitModal } from '../../components/ScheduleVisitModal';
import { BOOKING_SOURCES, BOOKING_SOURCE_LABELS, isBookingSource } from '../../lib/attribution';
import { ServiceRequestExpense } from './types';
import { buildExpenseContextFields } from '../../lib/adminExpenses';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/constants';

interface Visit {
  id: string;
  home_id: string;
  visit_type: 'maintenance' | 'service' | 'walkthrough';
  status: 'requested' | 'proposed' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
  scheduled_date: string | null;
  estimated_duration_minutes: number;
  actual_start_time: string | null;
  actual_end_time: string | null;
  assigned_to: string | null;
  booked_by?: string | null;
  booking_source?: 'admin_manual' | 'homeowner_request' | 'automation' | null;
  service_provider_id: string | null;
  internal_name: string | null;
  visit_notes: string | null;
  contractor_notes: string | null;
  provider_notes: string | null;
  homeowner_notes: string | null;
  homeowner_submitted_at: string | null;
  cost: number | null;
  created_at: string;
  // Flattened fields from visits_with_details view
  address: string;
  city: string;
  state: string;
  zip_code: string;
  owner_name: string;
  owner_phone: string;
  home_manager: string;
  service_provider_name: string | null;
  service_provider_phone: string | null;
  service_provider_email: string | null;
  total_tasks: number;
  completed_tasks: number;
  photo_count: number;
  walkthrough_visit_data: unknown | null;
  maintenance_visit_data?: unknown | null;
}

interface VisitTask {
  id: string;
  visit_id: string;
  task_id: string | null;
  task_type: 'user_task' | 'maintenance_task' | 'service_request';
  task_title: string;
  task_description: string | null;
  status: 'pending' | 'completed' | 'skipped';
  notes: string | null;
  estimated_duration_minutes: number | null;
  actual_duration_minutes: number | null;
  requested_by_homeowner: boolean | null;
  homeowner_notes: string | null;
  homeowner_priority: number | null;
}

interface VisitPhoto {
  id: string;
  visit_id: string;
  photo_url: string;
  description: string | null;
  photo_type: 'before' | 'during' | 'after' | 'general' | 'issue' | 'completion';
  taken_at: string;
}

interface NewTask {
  task_title: string;
  task_description: string;
  task_type: 'user_task' | 'maintenance_task' | 'service_request';
  estimated_duration_minutes: number;
  task_id?: string | null;
}

interface ServiceRequest {
  id: string;
  title: string;
  description: string | null;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  status: string;
}

interface MaintenanceItem {
  id: string;
  name: string;
  description: string | null;
  priority: 'high' | 'normal' | 'low';
  next_due_date: string;
  status: string;
  task_id: string;
  estimated_duration_minutes: number | null;
}

interface ScheduledMaintenanceVisit {
  id: string;
  scheduled_date: string | null;
  status: string;
  assigned_to: string | null;
  estimated_duration_minutes: number | null;
}

type VisitExpense = ServiceRequestExpense;

type VisitExpenseFormData = {
  parent_expense_id: string;
  amount: string;
  internal_cost: string;
  homeowner_visible: boolean;
  description: string;
  expense_date: string;
  category: string;
  vendor: string;
  billable_to_homeowner: boolean;
  needs_contractor_payment: boolean;
};

type VisitWizardLineItem = {
  tempId: string;
  description: string;
  category: string;
  vendor: string;
  amount: string;
  internal_cost: string;
  homeowner_visible: boolean;
};

const getDefaultVisitExpenseFormData = (): VisitExpenseFormData => ({
  parent_expense_id: '',
  amount: '',
  internal_cost: '',
  homeowner_visible: true,
  description: '',
  expense_date: '',
  category: '',
  vendor: '',
  billable_to_homeowner: true,
  needs_contractor_payment: true,
});

const getDefaultVisitWizardLineItemDraft = (): Omit<VisitWizardLineItem, 'tempId'> => ({
  description: '',
  category: '',
  vendor: '',
  amount: '',
  internal_cost: '',
  homeowner_visible: true,
});

interface WalkthroughPlan {
  id: string;
  next_due_date: string | null;
  status: string;
  is_custom: boolean;
  custom_task_name: string | null;
  custom_description: string | null;
  custom_frequency_months: number | null;
  maintenance_tasks: {
    name: string;
    description: string | null;
    frequency_months: number;
  } | null;
}

type VisitDetailWidget =
  | 'visitProgress'
  | 'homeHomeowner'
  | 'scheduling'
  | 'provider'
  | 'internalNotes'
  | 'maintenanceWorkflow'
  | 'tasks'
  | 'photos';

const DEFAULT_WIDGET_OPEN: Record<VisitDetailWidget, boolean> = {
  visitProgress: true,
  homeHomeowner: true,
  scheduling: true,
  provider: true,
  internalNotes: true,
  maintenanceWorkflow: true,
  tasks: true,
  photos: true,
};

const CONTRACTOR_NOTES_DEBOUNCE_MS = 800;

const normalizeVisitDurationMinutes = (value: number | null | undefined) =>
  typeof value === 'number' && isValidVisitDurationMinutes(value)
    ? value
    : DEFAULT_VISIT_DURATION_MINUTES;

interface MaintenanceVisitData {
  checklistStatus: Record<string, boolean>;
  selectedFrequencies: Record<string, MaintenanceFrequencyOption | null>;
  updated_at: string | null;
}

const getDefaultMaintenanceVisitData = (): MaintenanceVisitData => ({
  checklistStatus: {},
  selectedFrequencies: {},
  updated_at: null,
});

const normalizeMaintenanceVisitData = (raw: unknown): MaintenanceVisitData => {
  if (!raw || typeof raw !== 'object') {
    return getDefaultMaintenanceVisitData();
  }

  const rawData = raw as Partial<MaintenanceVisitData>;
  const checklistStatusEntries = Object.entries(rawData.checklistStatus ?? {}).filter(
    ([, value]) => typeof value === 'boolean'
  ) as Array<[string, boolean]>;
  const selectedFrequencyEntries = Object.entries(rawData.selectedFrequencies ?? {}).filter(
    ([, value]) =>
      value === null ||
      value === 'monthly' ||
      value === 'quarterly' ||
      value === 'biannual' ||
      value === 'annual'
  ) as Array<[string, MaintenanceFrequencyOption | null]>;

  return {
    checklistStatus: Object.fromEntries(checklistStatusEntries),
    selectedFrequencies: Object.fromEntries(selectedFrequencyEntries),
    updated_at: typeof rawData.updated_at === 'string' ? rawData.updated_at : null,
  };
};

function CollapsibleWidget({
  title,
  open,
  onToggle,
  children,
  headerRight,
  card = false,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
  /** White card + shadow (Visit Progress only) */
  card?: boolean;
}) {
  const headerAndBody = (
    <>
      <div className={`flex items-center justify-between gap-2 ${open ? 'pb-2' : 'pb-4'}`}>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className={`flex min-w-0 flex-1 items-center gap-2 rounded-md py-1 text-left ${
            card ? 'hover:bg-gray-100' : 'hover:bg-gray-100/60'
          }`}
        >
          <ChevronDown
            className={`h-5 w-5 shrink-0 text-gray-500 transition-transform ${open ? '' : '-rotate-90'}`}
            aria-hidden
          />
          <h2 className="text-lg font-semibold">{title}</h2>
        </button>
        {headerRight}
      </div>
      {open && <div className="pt-2">{children}</div>}
    </>
  );

  if (card) {
    return (
      <div className="py-8 first:pt-6">
        <div className="rounded-lg bg-white p-6 shadow">{headerAndBody}</div>
      </div>
    );
  }

  return <div className="py-8 first:pt-6 px-6">{headerAndBody}</div>;
}

const VisitDetail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const visitId = searchParams.get('id');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visit, setVisit] = useState<Visit | null>(null);
  const [tasks, setTasks] = useState<VisitTask[]>([]);
  const [photos, setPhotos] = useState<VisitPhoto[]>([]);
  const [serviceProviders, setServiceProviders] = useState<ServiceProvider[]>([]);
  const [admins, setAdmins] = useState<Array<{ id: string; full_name: string }>>([]);
  const [serviceProviderSearch, setServiceProviderSearch] = useState('');
  const [showServiceProviderDropdown, setShowServiceProviderDropdown] = useState(false);
  const [selectedServiceProviderIndex, setSelectedServiceProviderIndex] = useState(-1);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [newTask, setNewTask] = useState<NewTask>({
    task_title: '',
    task_description: '',
    task_type: 'user_task',
    estimated_duration_minutes: 60,
    task_id: null
  });
  const [addingTask, setAddingTask] = useState(false);
  const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
  const [loadingServiceRequests, setLoadingServiceRequests] = useState(false);
  const [selectedServiceRequestId, setSelectedServiceRequestId] = useState<string | null>(null);
  const [maintenanceItems, setMaintenanceItems] = useState<MaintenanceItem[]>([]);
  const [loadingMaintenanceItems, setLoadingMaintenanceItems] = useState(false);
  const [selectedMaintenanceItemId, setSelectedMaintenanceItemId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [deletingTask, setDeletingTask] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [visitTypeUpdating, setVisitTypeUpdating] = useState(false);
  const [shareMessage, setShareMessage] = useState<string | null>(null);
  const [shareMessageType, setShareMessageType] = useState<'success' | 'error'>('success');
  const [generatingContractorLink, setGeneratingContractorLink] = useState(false);
  const [timeEditMode, setTimeEditMode] = useState(false);
  const [timeDraftScheduledDate, setTimeDraftScheduledDate] = useState('');
  const [timeDraftDurationMinutes, setTimeDraftDurationMinutes] = useState(DEFAULT_VISIT_DURATION_MINUTES);
  const [savingTimeUpdates, setSavingTimeUpdates] = useState(false);
  const [internalNameDraft, setInternalNameDraft] = useState('');
  const internalNameFocusedRef = useRef(false);
  const savingInternalNameRef = useRef(false);
  const [internalNameSaveState, setInternalNameSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const internalNameSavedClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [contractorNotesDraft, setContractorNotesDraft] = useState('');
  const contractorNotesFocusedRef = useRef(false);
  const savingContractorNotesRef = useRef(false);
  const contractorNotesDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contractorNotesSavedClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [contractorNotesSaveState, setContractorNotesSaveState] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');
  const [contractorNotesSaveError, setContractorNotesSaveError] = useState<string | null>(null);
  const [widgetOpen, setWidgetOpen] = useState<Record<VisitDetailWidget, boolean>>(DEFAULT_WIDGET_OPEN);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoType, setPhotoType] = useState<'before' | 'during' | 'after' | 'general' | 'issue' | 'completion'>('general');
  const [photoDescription, setPhotoDescription] = useState('');
  const [photoUploadMessage, setPhotoUploadMessage] = useState<string | null>(null);
  const [photoUploadMessageType, setPhotoUploadMessageType] = useState<'success' | 'error'>('success');
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [maintenanceVisitData, setMaintenanceVisitData] = useState<MaintenanceVisitData>(
    getDefaultMaintenanceVisitData()
  );
  const [maintenanceDataSaveState, setMaintenanceDataSaveState] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');
  const [maintenanceDataSaveError, setMaintenanceDataSaveError] = useState<string | null>(null);
  const [showConfigureHomeSystemsModal, setShowConfigureHomeSystemsModal] = useState(false);
  const [walkthroughPlans, setWalkthroughPlans] = useState<WalkthroughPlan[]>([]);
  const [walkthroughPlansLoading, setWalkthroughPlansLoading] = useState(false);
  const [showScheduleVisitModal, setShowScheduleVisitModal] = useState(false);
  const [scheduledMaintenanceVisits, setScheduledMaintenanceVisits] = useState<ScheduledMaintenanceVisit[]>([]);
  const [visitExpenses, setVisitExpenses] = useState<VisitExpense[]>([]);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [savingExpense, setSavingExpense] = useState(false);
  const [editingExpense, setEditingExpense] = useState<VisitExpense | null>(null);
  const [lineItemParentExpense, setLineItemParentExpense] = useState<VisitExpense | null>(null);
  const [expenseWizardStep, setExpenseWizardStep] = useState(1);
  const [wizardLineItems, setWizardLineItems] = useState<VisitWizardLineItem[]>([]);
  const [wizardLineItemDraft, setWizardLineItemDraft] = useState<Omit<VisitWizardLineItem, 'tempId'>>(
    getDefaultVisitWizardLineItemDraft()
  );
  const [showWizardAddLineItemForm, setShowWizardAddLineItemForm] = useState(false);
  const [wizardAmountOverride, setWizardAmountOverride] = useState(false);
  const [expenseFormData, setExpenseFormData] = useState<VisitExpenseFormData>(getDefaultVisitExpenseFormData());
  const [showAddCustomTask, setShowAddCustomTask] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskFrequency, setNewTaskFrequency] = useState(1);
  const [savingCustomTask, setSavingCustomTask] = useState(false);

  // Invoice state
  const [selectedExpenseIdsForInvoice, setSelectedExpenseIdsForInvoice] = useState<Set<string>>(new Set());
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceDueDate, setInvoiceDueDate] = useState('');
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [invoiceSuccess, setInvoiceSuccess] = useState<string | null>(null);

  const toggleWidget = (key: VisitDetailWidget) => {
    setWidgetOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    if (!visitId) {
      navigate('/admin?tab=visits');
      return;
    }
    fetchVisitData();
    fetchServiceProviders();
    fetchAdmins();
  }, [visitId]);

  // Update service provider search text when visit or service providers load
  useEffect(() => {
    if (visit && serviceProviders.length > 0) {
      if (visit.service_provider_id) {
        const provider = serviceProviders.find(p => p.id === visit.service_provider_id);
        if (provider) {
          setServiceProviderSearch(provider.phone 
            ? `${provider.company_name} - ${provider.phone}`
            : provider.company_name);
        } else {
          setServiceProviderSearch('');
        }
      } else {
        setServiceProviderSearch('');
      }
    }
  }, [visit?.service_provider_id, serviceProviders]);

  useEffect(() => {
    if (!visit || contractorNotesFocusedRef.current) return;
    setContractorNotesDraft(visit.contractor_notes ?? '');
  }, [visit?.id, visit?.contractor_notes]);

  useEffect(() => {
    if (!visit || internalNameFocusedRef.current || savingInternalNameRef.current) return;
    setInternalNameDraft(visit.internal_name ?? '');
  }, [visit?.id, visit?.internal_name]);

  useEffect(() => {
    return () => {
      if (contractorNotesSavedClearRef.current) {
        clearTimeout(contractorNotesSavedClearRef.current);
      }
      if (contractorNotesDebounceRef.current) {
        clearTimeout(contractorNotesDebounceRef.current);
      }
      if (internalNameSavedClearRef.current) {
        clearTimeout(internalNameSavedClearRef.current);
      }
    };
  }, []);

  const fetchVisitData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch visit details
      const { data: viewVisitData, error: visitError } = await supabase
        .from('visits_with_details')
        .select('*')
        .eq('id', visitId)
        .single();

      let visitData: any = viewVisitData;

      // Fallback for environments where visits_with_details is stale or unavailable.
      if (visitError || !viewVisitData) {
        const { data: baseVisit, error: baseVisitError } = await supabase
          .from('visits')
          .select('*')
          .eq('id', visitId)
          .single();

        if (baseVisitError || !baseVisit) {
          throw visitError || baseVisitError;
        }

        const { data: homeData, error: homeError } = await supabase
          .from('homes')
          .select('address, city, state, zip_code, owner_id')
          .eq('id', baseVisit.home_id)
          .single();
        if (homeError || !homeData) {
          throw homeError || new Error('Could not load home details for visit.');
        }

        const { data: ownerData, error: ownerError } = await supabase
          .from('profiles')
          .select('full_name, phone_number, home_manager')
          .eq('id', homeData.owner_id)
          .single();
        if (ownerError || !ownerData) {
          throw ownerError || new Error('Could not load homeowner details for visit.');
        }

        let serviceProviderData: {
          company_name: string | null;
          phone: string | null;
          email: string | null;
        } | null = null;
        if (baseVisit.service_provider_id) {
          const { data: providerData, error: providerError } = await supabase
            .from('service_providers')
            .select('company_name, phone, email')
            .eq('id', baseVisit.service_provider_id)
            .maybeSingle();
          if (providerError) throw providerError;
          serviceProviderData = providerData || null;
        }

        visitData = {
          ...baseVisit,
          address: homeData.address,
          city: homeData.city,
          state: homeData.state,
          zip_code: homeData.zip_code,
          owner_name: ownerData.full_name,
          owner_phone: ownerData.phone_number,
          home_manager: ownerData.home_manager,
          service_provider_name: serviceProviderData?.company_name ?? null,
          service_provider_phone: serviceProviderData?.phone ?? null,
          service_provider_email: serviceProviderData?.email ?? null,
          total_tasks: 0,
          completed_tasks: 0,
          photo_count: 0,
        };
      }

      // Fetch visit tasks
      const { data: tasksData, error: tasksError } = await supabase
        .from('visit_tasks')
        .select('*')
        .eq('visit_id', visitId)
        .order('created_at');

      if (tasksError) throw tasksError;

      // Fetch visit photos
      const { data: photosData, error: photosError } = await supabase
        .from('visit_photos')
        .select('*')
        .eq('visit_id', visitId)
        .order('taken_at');

      if (photosError) throw photosError;

      setVisit({
        ...visitData,
        estimated_duration_minutes: normalizeVisitDurationMinutes(visitData.estimated_duration_minutes),
        total_tasks: tasksData?.length ?? 0,
        completed_tasks: (tasksData || []).filter((task) => task.status === 'completed').length,
        photo_count: photosData?.length ?? 0,
      });
      setMaintenanceVisitData(
        normalizeMaintenanceVisitData(
          visitData.walkthrough_visit_data ?? visitData.maintenance_visit_data
        )
      );
      if (visitData.visit_type === 'walkthrough') {
        void fetchWalkthroughPlans(visitData.home_id);
        void fetchScheduledMaintenanceVisits(visitData.home_id, visitData.id);
      }
      setTasks(tasksData || []);
      setPhotos(photosData || []);
      if (visitData?.id) {
        await fetchVisitExpenses(visitData.id);
      }
    } catch (error: any) {
      console.error('Error fetching visit data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceProviders = async () => {
    try {
      const { data, error } = await supabase
        .from('service_providers')
        .select('*')
        .order('company_name');

      if (error) throw error;
      setServiceProviders(data || []);
    } catch (error: any) {
      console.error('Error fetching service providers:', error);
    }
  };

  const fetchAdmins = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('is_system_manager', true)
        .order('full_name');

      if (error) throw error;
      setAdmins(data || []);
    } catch (error: any) {
      console.error('Error fetching admins:', error);
    }
  };

  const handleServiceProviderUpdate = async (serviceProviderId: string) => {
    try {
      setError(null);
      const providerId = serviceProviderId || null;

      const { error } = await supabase
        .from('visits')
        .update({
          service_provider_id: providerId,
          updated_at: new Date().toISOString()
        })
        .eq('id', visitId);

      if (error) throw error;

      // Update search text
      if (providerId) {
        const provider = serviceProviders.find(p => p.id === providerId);
        if (provider) {
          setServiceProviderSearch(provider.phone 
            ? `${provider.company_name} - ${provider.phone}`
            : provider.company_name);
        }
      } else {
        setServiceProviderSearch('');
      }

      // Optimistically update local state
      if (visit) {
        const selectedProvider = providerId ? serviceProviders.find(p => p.id === providerId) : null;
        setVisit({
          ...visit,
          service_provider_id: providerId,
          service_provider_name: selectedProvider?.company_name || null,
          service_provider_phone: selectedProvider?.phone || null,
          service_provider_email: selectedProvider?.email || null,
        });
      }

      setShowServiceProviderDropdown(false);
    } catch (error: any) {
      console.error('Error updating service provider:', error);
      setError(error.message);
      // Refresh data on error to revert optimistic update
      await fetchVisitData();
    }
  };

  // Filter service providers based on search
  const filteredServiceProviders = serviceProviders.filter(provider => {
    if (!serviceProviderSearch.trim()) return true;
    const searchLower = serviceProviderSearch.toLowerCase();
    const companyMatch = provider.company_name.toLowerCase().includes(searchLower);
    const phoneMatch = provider.phone?.toLowerCase().includes(searchLower);
    const emailMatch = provider.email?.toLowerCase().includes(searchLower);
    return companyMatch || phoneMatch || emailMatch;
  });

  const handleCostUpdate = async (cost: number | null) => {
    try {
      setError(null);

      const { error } = await supabase
        .from('visits')
        .update({
          cost: cost,
          updated_at: new Date().toISOString()
        })
        .eq('id', visitId);

      if (error) throw error;

      // Optimistically update local state
      if (visit) {
        setVisit({ ...visit, cost });
      }
    } catch (error: any) {
      console.error('Error updating cost:', error);
      setError(error.message);
      // Refresh data on error to revert optimistic update
      await fetchVisitData();
    }
  };

  const resetExpenseForm = () => {
    setEditingExpense(null);
    setLineItemParentExpense(null);
    setExpenseFormData({
      ...getDefaultVisitExpenseFormData(),
      expense_date: new Date().toISOString().split('T')[0],
    });
    setExpenseWizardStep(1);
    setWizardLineItems([]);
    setWizardLineItemDraft(getDefaultVisitWizardLineItemDraft());
    setShowWizardAddLineItemForm(false);
    setWizardAmountOverride(false);
  };

  const fetchVisitExpenses = async (currentVisitId: string) => {
    const { data, error: expenseError } = await supabase
      .from('service_request_expenses')
      .select('*')
      .eq('visit_id', currentVisitId)
      .order('created_at', { ascending: false });

    if (expenseError) throw expenseError;
    setVisitExpenses((data || []) as VisitExpense[]);
  };

  const handleOpenExpenseModal = (expense?: VisitExpense, parentExpense?: VisitExpense) => {
    setError(null);
    setWizardLineItems([]);
    setWizardLineItemDraft(getDefaultVisitWizardLineItemDraft());
    setShowWizardAddLineItemForm(false);
    setWizardAmountOverride(false);

    if (expense) {
      setEditingExpense(expense);
      setLineItemParentExpense(null);
      setExpenseFormData({
        parent_expense_id: expense.parent_expense_id || '',
        amount: String(expense.amount ?? ''),
        internal_cost:
          expense.internal_cost !== null && expense.internal_cost !== undefined
            ? String(expense.internal_cost)
            : '',
        homeowner_visible: expense.homeowner_visible,
        description: expense.description || '',
        expense_date: expense.expense_date || '',
        category: expense.category || '',
        vendor: expense.vendor || '',
        billable_to_homeowner: expense.billable_to_homeowner,
        needs_contractor_payment: expense.needs_contractor_payment,
      });
      setExpenseWizardStep(2);
    } else {
      setEditingExpense(null);
      setLineItemParentExpense(parentExpense || null);
      setExpenseFormData({
        ...getDefaultVisitExpenseFormData(),
        parent_expense_id: parentExpense?.id || '',
        expense_date: new Date().toISOString().split('T')[0],
      });
      setExpenseWizardStep(1);
    }
    setShowExpenseModal(true);
  };

  const handleSaveVisitExpense = async () => {
    if (!visit) return;

    try {
      setSavingExpense(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const contextFields = buildExpenseContextFields({
        type: 'visit',
        visitId: visit.id,
        homeId: visit.home_id,
      });

      const isNewParentWithLineItems = !editingExpense && !lineItemParentExpense && wizardLineItems.length > 0;

      if (isNewParentWithLineItems) {
        const computedTotal = wizardLineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
        const parentAmount = wizardAmountOverride && expenseFormData.amount.trim()
          ? parseFloat(expenseFormData.amount)
          : computedTotal;

        if (Number.isNaN(parentAmount) || parentAmount <= 0) {
          setError('Total expense amount must be greater than zero.');
          return;
        }

        const parentPayload = {
          ...contextFields,
          amount: parentAmount,
          description: expenseFormData.description || null,
          expense_date: expenseFormData.expense_date || null,
          category: expenseFormData.category || null,
          vendor: expenseFormData.vendor || null,
          parent_expense_id: null,
          quote_id: null,
          billable_to_homeowner: expenseFormData.billable_to_homeowner,
          needs_contractor_payment: expenseFormData.needs_contractor_payment,
          homeowner_visible: true,
          internal_cost: null,
          created_by: user.id,
        };

        const { data: parentExpense, error: parentError } = await supabase
          .from('service_request_expenses')
          .insert([parentPayload])
          .select('id')
          .single();

        if (parentError) throw parentError;

        const childRows = wizardLineItems.map((item) => ({
          ...contextFields,
          parent_expense_id: parentExpense.id,
          quote_id: null,
          amount: parseFloat(item.amount) || 0,
          internal_cost: item.internal_cost.trim() ? parseFloat(item.internal_cost) : null,
          homeowner_visible: item.homeowner_visible,
          description: item.description || null,
          expense_date: expenseFormData.expense_date || null,
          category: item.category || null,
          vendor: item.vendor || null,
          billable_to_homeowner: expenseFormData.billable_to_homeowner,
          needs_contractor_payment: expenseFormData.needs_contractor_payment,
          created_by: user.id,
        }));

        const { error: childError } = await supabase.from('service_request_expenses').insert(childRows);
        if (childError) throw childError;
      } else {
        const parsedAmount = parseFloat(expenseFormData.amount);
        if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
          setError('Expense amount must be greater than zero.');
          return;
        }

        const payload = {
          ...contextFields,
          amount: parsedAmount,
          description: expenseFormData.description || null,
          expense_date: expenseFormData.expense_date || null,
          category: expenseFormData.category || null,
          vendor: expenseFormData.vendor || null,
          parent_expense_id: expenseFormData.parent_expense_id || null,
          quote_id: null,
          billable_to_homeowner: expenseFormData.billable_to_homeowner,
          needs_contractor_payment: expenseFormData.needs_contractor_payment,
          homeowner_visible: expenseFormData.homeowner_visible,
          internal_cost: expenseFormData.internal_cost.trim() ? parseFloat(expenseFormData.internal_cost) : null,
          created_by: user.id,
        };

        if (editingExpense) {
          const { error: updateError } = await supabase
            .from('service_request_expenses')
            .update(payload)
            .eq('id', editingExpense.id);
          if (updateError) throw updateError;
        } else {
          const { error: createError } = await supabase
            .from('service_request_expenses')
            .insert([payload]);
          if (createError) throw createError;
        }
      }

      setShowExpenseModal(false);
      resetExpenseForm();
      await fetchVisitExpenses(visit.id);
    } catch (saveError: any) {
      console.error('Error saving visit expense:', saveError);
      setError(saveError.message || 'Failed to save expense');
    } finally {
      setSavingExpense(false);
    }
  };

  const computedWizardLineItemsTotal = wizardLineItems.reduce(
    (sum, item) => sum + (parseFloat(item.amount) || 0),
    0
  );
  const computedWizardInternalTotal = wizardLineItems.reduce(
    (sum, item) => sum + (parseFloat(item.internal_cost) || 0),
    0
  );
  const parsedWizardAmount = parseFloat(expenseFormData.amount);
  const isNewParentFlow = !editingExpense && !lineItemParentExpense;
  const isLineItemFlow = !!lineItemParentExpense || (!!editingExpense && !!editingExpense.parent_expense_id);
  const step2Valid = isNewParentFlow
    ? wizardLineItems.length > 0
    : !!(expenseFormData.description || expenseFormData.amount);
  const step3Valid = isNewParentFlow
    ? !wizardAmountOverride || (!!expenseFormData.amount && !Number.isNaN(parsedWizardAmount) && parsedWizardAmount > 0)
    : !!expenseFormData.amount.trim();

  const handleSaveVisitExpenseWizard = async () => {
    if (expenseWizardStep < 4) {
      if (expenseWizardStep === 2 && !step2Valid) {
        setError('Add at least one cost component before continuing.');
        return;
      }
      if (expenseWizardStep === 3 && !step3Valid) {
        setError('Enter a valid amount before continuing.');
        return;
      }
      setExpenseWizardStep((prev) => prev + 1);
      return;
    }

    await handleSaveVisitExpense();
  };

  const parentVisitExpenses = visitExpenses.filter((expense) => !expense.parent_expense_id);
  const getChildVisitExpenses = (parentId: string) =>
    visitExpenses.filter((expense) => expense.parent_expense_id === parentId);

  const handleDeleteVisitExpense = async (expenseId: string) => {
    if (!visit) return;
    try {
      setError(null);
      const { error: deleteError } = await supabase
        .from('service_request_expenses')
        .delete()
        .eq('id', expenseId);
      if (deleteError) throw deleteError;
      await fetchVisitExpenses(visit.id);
    } catch (deleteError: any) {
      console.error('Error deleting visit expense:', deleteError);
      setError(deleteError.message || 'Failed to delete expense');
    }
  };

  const handleOpenInvoiceModal = () => {
    const defaultDueDate = new Date();
    defaultDueDate.setDate(defaultDueDate.getDate() + 30);
    setInvoiceDueDate(defaultDueDate.toISOString().split('T')[0]);
    setInvoiceNotes('');
    setInvoiceError(null);
    setShowInvoiceModal(true);
  };

  const handleCreateInvoice = async () => {
    if (!visit || selectedExpenseIdsForInvoice.size === 0) return;

    setCreatingInvoice(true);
    setInvoiceError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const selectedExpenses = parentVisitExpenses.filter(
        (exp) => selectedExpenseIdsForInvoice.has(exp.id)
      );

      const nonBillable = selectedExpenses.filter((exp) => !exp.billable_to_homeowner);
      if (nonBillable.length > 0) {
        throw new Error('Selected expenses include non-billable items. Only homeowner-billable expenses can be invoiced.');
      }

      const { data: homeData, error: homeError } = await supabase
        .from('homes')
        .select('owner_id')
        .eq('id', visit.home_id)
        .single();

      if (homeError || !homeData) throw new Error('Failed to fetch homeowner information');

      const homeownerId = homeData.owner_id;

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('stripe_customer_id')
        .eq('id', homeownerId)
        .single();

      if (profileError) throw new Error('Failed to fetch homeowner profile');
      if (!profileData?.stripe_customer_id) {
        throw new Error('Homeowner does not have a Stripe customer account. Please create one first in their profile.');
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const functionUrl = `${SUPABASE_URL}/functions/v1/create-stripe-invoice`;
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          homeownerId,
          expenseIds: Array.from(selectedExpenseIdsForInvoice),
          dueDate: invoiceDueDate || undefined,
          notes: invoiceNotes || undefined,
        }),
      });

      const data = await response.json();
      if (!response.ok || data.error) throw new Error(data.error || 'Failed to create invoice');

      await fetchVisitExpenses(visit.id);
      setSelectedExpenseIdsForInvoice(new Set());
      setShowInvoiceModal(false);

      const total = selectedExpenses.reduce((sum, exp) => sum + Number(exp.amount), 0);
      setInvoiceSuccess(
        `Invoice created for ${selectedExpenses.length} expense${selectedExpenses.length === 1 ? '' : 's'} totalling $${total.toFixed(2)}. The homeowner can now view and pay it.`
      );
      setTimeout(() => setInvoiceSuccess(null), 6000);
    } catch (err: any) {
      console.error('Error creating invoice:', err);
      setInvoiceError(err.message || 'Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleInternalNameSave = async (rawValue: string) => {
    if (!visitId) return;

    const trimmed = rawValue.trim();
    const nextValue = trimmed.length === 0 ? null : trimmed;
    const currentValue = visit?.internal_name ?? null;
    if (nextValue === currentValue) {
      setInternalNameDraft(trimmed);
      return;
    }

    try {
      savingInternalNameRef.current = true;
      setInternalNameSaveState('saving');
      setError(null);

      const { error: updateError } = await supabase
        .from('visits')
        .update({
          internal_name: nextValue,
          updated_at: new Date().toISOString(),
        })
        .eq('id', visitId);

      if (updateError) throw updateError;

      setInternalNameDraft(trimmed);
      if (visit) {
        setVisit({ ...visit, internal_name: nextValue });
      }
      setInternalNameSaveState('saved');
      if (internalNameSavedClearRef.current) {
        clearTimeout(internalNameSavedClearRef.current);
      }
      internalNameSavedClearRef.current = setTimeout(() => {
        setInternalNameSaveState((prev) => (prev === 'saved' ? 'idle' : prev));
      }, 1500);
    } catch (saveError: any) {
      console.error('Error updating internal name:', saveError);
      setInternalNameSaveState('error');
      setError(saveError.message ?? 'Failed to save internal name.');
    } finally {
      savingInternalNameRef.current = false;
    }
  };

  const handleInternalNotesUpdate = async (notes: string) => {
    try {
      setError(null);

      const { error } = await supabase
        .from('visits')
        .update({
          visit_notes: notes || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', visitId);

      if (error) throw error;

      // Optimistically update local state
      if (visit) {
        setVisit({ ...visit, visit_notes: notes || null });
      }
    } catch (error: any) {
      console.error('Error updating internal notes:', error);
      setError(error.message);
      // Refresh data on error to revert optimistic update
      await fetchVisitData();
    }
  };

  const handleBookingAttributionUpdate = async (updates: {
    booked_by?: string | null;
    booking_source?: 'admin_manual' | 'homeowner_request' | 'automation' | null;
  }) => {
    if (
      !visitId ||
      !visit ||
      (visit.visit_type !== 'walkthrough' && visit.visit_type !== 'maintenance')
    ) {
      return;
    }
    try {
      setError(null);
      const { error: updateError } = await supabase
        .from('visits')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', visitId);
      if (updateError) throw updateError;
      if (visit) {
        setVisit({ ...visit, ...updates });
      }
    } catch (err: any) {
      console.error('Error updating booking attribution:', err);
      setError(err?.message || 'Failed to update booking attribution');
      await fetchVisitData();
    }
  };

  const saveMaintenanceVisitData = async (nextData: MaintenanceVisitData) => {
    if (!visitId) return;

    try {
      setMaintenanceDataSaveState('saving');
      setMaintenanceDataSaveError(null);
      setError(null);

      const payload = {
        ...nextData,
        updated_at: new Date().toISOString(),
      };

      const { error: updateError } = await supabase
        .from('visits')
        .update({
          walkthrough_visit_data: payload,
          updated_at: new Date().toISOString(),
        })
        .eq('id', visitId);

      if (updateError) throw updateError;

      setMaintenanceVisitData(payload);
      setVisit((prev) =>
        prev
          ? {
              ...prev,
              walkthrough_visit_data: payload,
            }
          : prev
      );
      setMaintenanceDataSaveState('saved');
      setTimeout(() => {
        setMaintenanceDataSaveState((prev) => (prev === 'saved' ? 'idle' : prev));
      }, 2000);
    } catch (saveError: any) {
      console.error('Error saving maintenance visit data:', saveError);
      setMaintenanceDataSaveState('error');
      setMaintenanceDataSaveError(saveError.message || 'Failed to save walkthrough workflow changes.');
      setError(saveError.message);
      await fetchVisitData();
    }
  };

  const toggleMaintenanceChecklistItem = (itemId: string) => {
    const nextData: MaintenanceVisitData = {
      ...maintenanceVisitData,
      checklistStatus: {
        ...maintenanceVisitData.checklistStatus,
        [itemId]: !maintenanceVisitData.checklistStatus[itemId],
      },
    };
    setMaintenanceVisitData(nextData);
    void saveMaintenanceVisitData(nextData);
  };

  const handleDeleteVisit = async () => {
    try {
      setDeleting(true);
      setError(null);

      const { error } = await supabase
        .from('visits')
        .delete()
        .eq('id', visitId);

      if (error) throw error;

      // Navigate back to visits list
      navigate('/admin?tab=visits');
    } catch (error: any) {
      console.error('Error deleting visit:', error);
      setError(error.message);
      setDeleting(false);
    }
  };

  const handleTaskStatusUpdate = async (taskId: string, newStatus: VisitTask['status']) => {
    try {
      const updateData: any = { 
        status: newStatus,
        updated_at: new Date().toISOString()
      };

      // If completing a task, set actual duration if not already set
      if (newStatus === 'completed') {
        const task = tasks.find(t => t.id === taskId);
        if (task && !task.actual_duration_minutes && task.estimated_duration_minutes) {
          updateData.actual_duration_minutes = task.estimated_duration_minutes;
        }
      }

      const { error } = await supabase
        .from('visit_tasks')
        .update(updateData)
        .eq('id', taskId);

      if (error) throw error;

      // Update local state
      setTasks(prevTasks =>
        prevTasks.map(task =>
          task.id === taskId
            ? { ...task, ...updateData }
            : task
        )
      );
    } catch (error: any) {
      console.error('Error updating task status:', error);
      setError(error.message);
    }
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;

    try {
      setDeletingTask(true);
      setError(null);

      const { error } = await supabase
        .from('visit_tasks')
        .delete()
        .eq('id', taskToDelete);

      if (error) throw error;

      // Update local state
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskToDelete));
      
      // Refresh visit data to update task counts
      await fetchVisitData();
      
      setTaskToDelete(null);
    } catch (error: any) {
      console.error('Error deleting task:', error);
      setError(error.message);
    } finally {
      setDeletingTask(false);
    }
  };

  const fetchServiceRequests = async () => {
    if (!visit?.home_id) return;
    
    try {
      setLoadingServiceRequests(true);
      const { data, error } = await supabase
        .from('service_requests')
        .select('id, title, description, priority, status')
        .eq('home_id', visit.home_id)
        .in('status', ['pending', 'in_progress'])
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Filter out service requests that are already linked to tasks in this visit
      const existingServiceRequestIds = tasks
        .filter(t => t.task_type === 'service_request' && t.task_id)
        .map(t => t.task_id);
      
      const filteredData = (data || []).filter(sr => !existingServiceRequestIds.includes(sr.id));
      setServiceRequests(filteredData);
    } catch (error: any) {
      console.error('Error fetching service requests:', error);
    } finally {
      setLoadingServiceRequests(false);
    }
  };

  const fetchMaintenanceItems = async () => {
    if (!visit?.home_id) return;
    
    try {
      setLoadingMaintenanceItems(true);
      const { data, error } = await supabase
        .from('maintenance_plans')
        .select(`
          id,
            next_due_date,
            status,
            task_id,
            maintenance_tasks!inner (
            id,
            name,
            description,
            estimated_duration_minutes
          )
        `)
        .eq('home_id', visit.home_id)
        .in('status', ['pending', 'due', 'overdue']);

      if (error) throw error;

      const maintenanceData = (data || []).map(plan => {
        const task = Array.isArray(plan.maintenance_tasks) 
          ? plan.maintenance_tasks[0] 
          : plan.maintenance_tasks;
        
        // Determine priority based on status
        let priority: 'high' | 'normal' | 'low' = 'normal';
        if (plan.status === 'overdue') {
          priority = 'high';
        } else if (plan.status === 'due') {
          priority = 'normal';
        } else {
          priority = 'low';
        }

        return {
          id: plan.id,
          name: task.name,
          description: task.description || null,
          priority,
          next_due_date: plan.next_due_date,
          status: plan.status,
          task_id: plan.task_id || task.id,
          estimated_duration_minutes: task.estimated_duration_minutes ?? null,
        };
      });

      // Filter out maintenance items that are already linked to tasks in this visit
      const existingMaintenanceTaskIds = tasks
        .filter(t => t.task_type === 'maintenance_task' && t.task_id)
        .map(t => t.task_id);
      
      const filteredData = maintenanceData.filter(item => 
        !existingMaintenanceTaskIds.includes(item.task_id)
      );

      setMaintenanceItems(filteredData);
    } catch (error: any) {
      console.error('Error fetching maintenance items:', error);
    } finally {
      setLoadingMaintenanceItems(false);
    }
  };

  const handlePlanFrequencyUpdate = async (planId: string, months: number) => {
    setWalkthroughPlans((prev) =>
      prev.map((p) => (p.id === planId ? { ...p, custom_frequency_months: months } : p))
    );
    try {
      const { error } = await supabase
        .from('maintenance_plans')
        .update({ custom_frequency_months: months, updated_at: new Date().toISOString() })
        .eq('id', planId);
      if (error) throw error;
    } catch (err: any) {
      console.error('Error updating plan frequency:', err);
      void fetchWalkthroughPlans(visit!.home_id);
    }
  };

  const fetchScheduledMaintenanceVisits = async (homeId: string, currentVisitId: string) => {
    try {
      const { data, error } = await supabase
        .from('visits')
        .select('id, scheduled_date, status, assigned_to, estimated_duration_minutes')
        .eq('home_id', homeId)
        .eq('visit_type', 'maintenance')
        .neq('id', currentVisitId)
        .not('status', 'in', '("cancelled","no_show")')
        .order('scheduled_date', { ascending: true });
      if (error) throw error;
      setScheduledMaintenanceVisits(data ?? []);
    } catch (err) {
      console.error('Error fetching scheduled maintenance visits:', err);
    }
  };

  const handleAddCustomTask = async () => {
    if (!newTaskName.trim() || !visit) return;
    setSavingCustomTask(true);
    try {
      const { error } = await supabase.from('maintenance_plans').insert({
        home_id: visit.home_id,
        is_custom: true,
        custom_task_name: newTaskName.trim(),
        custom_description: newTaskDescription.trim() || null,
        custom_frequency_months: newTaskFrequency,
        status: 'upcoming',
      });
      if (error) throw error;
      setNewTaskName('');
      setNewTaskDescription('');
      setNewTaskFrequency(1);
      setShowAddCustomTask(false);
      void fetchWalkthroughPlans(visit.home_id);
    } catch (err: any) {
      console.error('Error adding custom task:', err);
    } finally {
      setSavingCustomTask(false);
    }
  };

  const fetchWalkthroughPlans = async (homeId: string) => {
    try {
      setWalkthroughPlansLoading(true);
      const { data, error } = await supabase
        .from('maintenance_plans')
        .select(`
          id,
          next_due_date,
          status,
          is_custom,
          custom_task_name,
          custom_description,
          custom_frequency_months,
          maintenance_tasks (
            name,
            description,
            frequency_months
          )
        `)
        .eq('home_id', homeId)
        .order('next_due_date', { ascending: true });

      if (error) throw error;

      setWalkthroughPlans(
        (data || []).map((plan) => {
          const tasks = Array.isArray(plan.maintenance_tasks)
            ? plan.maintenance_tasks[0] ?? null
            : plan.maintenance_tasks ?? null;
          return {
            ...plan,
            custom_frequency_months:
              plan.custom_frequency_months != null ? Number(plan.custom_frequency_months) : null,
            maintenance_tasks: tasks
              ? { ...tasks, frequency_months: Number(tasks.frequency_months) }
              : null,
          };
        })
      );
    } catch (err: any) {
      console.error('Error fetching walkthrough plans:', err);
      setWalkthroughPlans([]);
    } finally {
      setWalkthroughPlansLoading(false);
    }
  };

  const handleServiceRequestSelect = (serviceRequestId: string | null) => {
    setSelectedServiceRequestId(serviceRequestId);
    setSelectedMaintenanceItemId(null); // Clear maintenance selection
    
    if (serviceRequestId) {
      const sr = serviceRequests.find(r => r.id === serviceRequestId);
      if (sr) {
        setNewTask({
          task_title: sr.title,
          task_description: sr.description || '',
          task_type: 'service_request',
          estimated_duration_minutes: 60,
          task_id: serviceRequestId
        });
      }
    } else {
      setNewTask({
        task_title: '',
        task_description: '',
        task_type: 'user_task',
        estimated_duration_minutes: 60,
        task_id: null
      });
    }
  };

  const handleMaintenanceItemSelect = (maintenanceItemId: string | null) => {
    setSelectedMaintenanceItemId(maintenanceItemId);
    setSelectedServiceRequestId(null); // Clear service request selection
    
    if (maintenanceItemId) {
      const item = maintenanceItems.find(m => m.id === maintenanceItemId);
      if (item) {
        setNewTask({
          task_title: item.name,
          task_description: item.description || '',
          task_type: 'maintenance_task',
          estimated_duration_minutes: item.estimated_duration_minutes ?? 60,
          task_id: item.task_id
        });
      }
    } else {
      setNewTask({
        task_title: '',
        task_description: '',
        task_type: 'user_task',
        estimated_duration_minutes: 60,
        task_id: null
      });
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !visit) return;

    setPhotoUploadMessage(null);
    setUploadingPhoto(true);

    try {
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const filePath = `${visit.id}/${timestamp}_${sanitizedName}`;

      const { error: uploadError } = await supabase.storage.from('visit-photos').upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from('visit-photos').getPublicUrl(filePath);

      const { data: insertedPhoto, error: insertError } = await supabase
        .from('visit_photos')
        .insert({
          visit_id: visit.id,
          photo_url: publicUrl,
          description: photoDescription.trim() || null,
          photo_type: photoType,
        })
        .select('*')
        .single();

      if (insertError) {
        await supabase.storage.from('visit-photos').remove([filePath]);
        throw insertError;
      }

      setPhotos((prev) => [...prev, insertedPhoto as VisitPhoto]);
      setPhotoDescription('');
      setPhotoUploadMessageType('success');
      setPhotoUploadMessage('Photo uploaded successfully.');
    } catch (err) {
      console.error('Photo upload error:', err);
      setPhotoUploadMessageType('error');
      setPhotoUploadMessage('Failed to upload photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
      event.target.value = '';
    }
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddingTask(true);
    setError(null);

    try {
      const taskData: any = {
        visit_id: visitId,
        task_title: newTask.task_title,
        task_description: newTask.task_description,
        task_type: newTask.task_type,
        estimated_duration_minutes: newTask.estimated_duration_minutes
      };

      // If it's a service request or maintenance task, include the task_id
      if ((newTask.task_type === 'service_request' || newTask.task_type === 'maintenance_task') && newTask.task_id) {
        taskData.task_id = newTask.task_id;
      }

      const { error } = await supabase
        .from('visit_tasks')
        .insert([taskData]);

      if (error) throw error;

      setShowAddTaskModal(false);
      setSelectedServiceRequestId(null);
      setSelectedMaintenanceItemId(null);
      setNewTask({
        task_title: '',
        task_description: '',
        task_type: 'user_task',
        estimated_duration_minutes: 60,
        task_id: null
      });
      
      await fetchVisitData();
    } catch (error: any) {
      console.error('Error adding task:', error);
      setError(error.message);
    } finally {
      setAddingTask(false);
    }
  };

  const handleStatusUpdate = async (newStatus: Visit['status']) => {
    try {
      setStatusUpdating(true);
      setError(null);

      const updateData: any = { 
        status: newStatus,
        updated_at: new Date().toISOString()
      };

      // Set timestamps based on status
      if (newStatus === 'in_progress' && !visit?.actual_start_time) {
        updateData.actual_start_time = new Date().toISOString();
      } else if (newStatus === 'completed' && !visit?.actual_end_time) {
        updateData.actual_end_time = new Date().toISOString();
      }

      const { error } = await supabase
        .from('visits')
        .update(updateData)
        .eq('id', visitId);

      if (error) throw error;

      await fetchVisitData();
    } catch (error: any) {
      console.error('Error updating visit status:', error);
      setError(error.message);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleVisitTypeUpdate = async (newType: Visit['visit_type']) => {
    if (!visit || newType === visit.visit_type) return;

    const typeLabel: Record<Visit['visit_type'], string> = {
      maintenance: 'Maintenance Visit',
      service: 'Service Visit',
      walkthrough: 'Walkthrough',
    };

    const confirmed = window.confirm(
      `Change this visit's type from "${typeLabel[visit.visit_type]}" to "${typeLabel[newType]}"?\n\n` +
        `This affects which workflow appears on the visit and how it's reported.`
    );
    if (!confirmed) return;

    try {
      setVisitTypeUpdating(true);
      setError(null);

      const { error: updateError } = await supabase
        .from('visits')
        .update({
          visit_type: newType,
          updated_at: new Date().toISOString(),
        })
        .eq('id', visitId);

      if (updateError) throw updateError;

      await fetchVisitData();
    } catch (err: any) {
      console.error('Error updating visit type:', err);
      setError(err.message || 'Failed to update visit type.');
    } finally {
      setVisitTypeUpdating(false);
    }
  };

  const beginTimeEdit = () => {
    if (!visit) return;
    setTimeDraftScheduledDate(toLocalDateTimeString(visit.scheduled_date));
    setTimeDraftDurationMinutes(normalizeVisitDurationMinutes(visit.estimated_duration_minutes));
    setTimeEditMode(true);
  };

  const cancelTimeEdit = () => {
    setTimeEditMode(false);
    if (!visit) return;
    setTimeDraftScheduledDate(toLocalDateTimeString(visit.scheduled_date));
    setTimeDraftDurationMinutes(normalizeVisitDurationMinutes(visit.estimated_duration_minutes));
  };

  const handleSaveTimeUpdates = async () => {
    try {
      setSavingTimeUpdates(true);
      setError(null);
      const scheduledDate = timeDraftScheduledDate
        ? new Date(timeDraftScheduledDate).toISOString()
        : null;

      const durationMinutes = normalizeVisitDurationMinutes(timeDraftDurationMinutes);

      const { error } = await supabase
        .from('visits')
        .update({
          scheduled_date: scheduledDate,
          estimated_duration_minutes: durationMinutes,
          updated_at: new Date().toISOString(),
        })
        .eq('id', visitId);

      if (error) throw error;

      if (visit) {
        setVisit({
          ...visit,
          scheduled_date: scheduledDate,
          estimated_duration_minutes: durationMinutes,
        });
      }
      setTimeEditMode(false);
    } catch (error: any) {
      console.error('Error saving time updates:', error);
      setError(error.message);
      await fetchVisitData();
    } finally {
      setSavingTimeUpdates(false);
    }
  };

  const handleAssignedToUpdate = async (adminId: string) => {
    try {
      setError(null);
      
      // Find the admin's name from the admins list
      const selectedAdmin = admins.find(admin => admin.id === adminId);
      const assignedTo = selectedAdmin ? selectedAdmin.full_name : null;

      const { error } = await supabase
        .from('visits')
        .update({
          assigned_to: assignedTo,
          updated_at: new Date().toISOString()
        })
        .eq('id', visitId);

      if (error) throw error;

      // Optimistically update local state
      if (visit) {
        setVisit({ ...visit, assigned_to: assignedTo });
      }
    } catch (error: any) {
      console.error('Error updating assigned to:', error);
      setError(error.message);
      // Refresh data on error to revert optimistic update
      await fetchVisitData();
    }
  };

  const handleContractorNotesUpdate = async (notes: string) => {
    if (!visitId) {
      setContractorNotesSaveState('error');
      setContractorNotesSaveError('Visit is not loaded.');
      return;
    }
    if (savingContractorNotesRef.current) return;
    if (visit && (notes || '') === (visit.contractor_notes || '')) {
      return;
    }
    savingContractorNotesRef.current = true;
    setContractorNotesSaveState('saving');
    setContractorNotesSaveError(null);
    try {
      setError(null);

      const { data, error } = await supabase
        .from('visits')
        .update({
          contractor_notes: notes || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', visitId)
        .select('id, contractor_notes');

      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error(
          'Could not save provider notes (no rows updated). Check that you have permission to edit this visit.'
        );
      }

      const row = data[0];
      setVisit((prev) =>
        prev ? { ...prev, contractor_notes: row.contractor_notes } : null
      );
      setContractorNotesDraft(row.contractor_notes ?? '');

      if (contractorNotesSavedClearRef.current) {
        clearTimeout(contractorNotesSavedClearRef.current);
      }
      setContractorNotesSaveState('saved');
      contractorNotesSavedClearRef.current = setTimeout(() => {
        setContractorNotesSaveState('idle');
        contractorNotesSavedClearRef.current = null;
      }, 3500);
    } catch (error: any) {
      console.error('Error updating contractor notes:', error);
      const msg =
        error?.message ||
        'Could not save provider notes. Please try again.';
      setContractorNotesSaveState('error');
      setContractorNotesSaveError(msg);
      setError(msg);
      await fetchVisitData();
    } finally {
      savingContractorNotesRef.current = false;
    }
  };

  const scheduleContractorNotesSave = (notes: string) => {
    if (contractorNotesDebounceRef.current) {
      clearTimeout(contractorNotesDebounceRef.current);
    }
    contractorNotesDebounceRef.current = setTimeout(() => {
      contractorNotesDebounceRef.current = null;
      void handleContractorNotesUpdate(notes);
    }, CONTRACTOR_NOTES_DEBOUNCE_MS);
  };

  const createContractorVisitLink = async (): Promise<{ url: string; expiresAt: string }> => {
    if (!visitId) {
      throw new Error('A visit id is required.');
    }

    const { data, error: invokeError } = await supabase.functions.invoke('generate-contractor-visit-link', {
      body: {
        visitId,
      },
    });

    if (invokeError) {
      throw new Error(invokeError.message || 'Failed to generate contractor link.');
    }

    if (!data?.url || !data?.expiresAt) {
      throw new Error('Contractor link response was incomplete.');
    }

    return {
      url: data.url as string,
      expiresAt: data.expiresAt as string,
    };
  };

  const handleOpenContractorView = async () => {
    setGeneratingContractorLink(true);
    try {
      const contractorLink = await createContractorVisitLink();
      if (typeof window !== 'undefined') {
        window.open(contractorLink.url, '_blank', 'noopener,noreferrer');
      }
      setShareMessageType('success');
      setShareMessage(`Opened contractor link. Expires ${new Date(contractorLink.expiresAt).toLocaleString()}.`);
    } catch (linkError: any) {
      console.error('Error opening contractor view:', linkError);
      setShareMessageType('error');
      setShareMessage(linkError?.message || 'Could not generate contractor link.');
    } finally {
      setGeneratingContractorLink(false);
    }
  };

  const handleCopyContractorLink = async () => {
    setGeneratingContractorLink(true);
    try {
      const contractorLink = await createContractorVisitLink();
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(contractorLink.url);
      } else if (typeof document !== 'undefined') {
        const textArea = document.createElement('textarea');
        textArea.value = contractorLink.url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      } else {
        throw new Error('Clipboard API unavailable');
      }

      setShareMessageType('success');
      setShareMessage(`Contractor link copied. Expires ${new Date(contractorLink.expiresAt).toLocaleString()}.`);
    } catch (copyError) {
      console.error('Error copying contractor link:', copyError);
      setShareMessageType('error');
      setShareMessage('Could not copy link. Please copy it directly from your browser.');
    } finally {
      setGeneratingContractorLink(false);
    }
  };

  const getTaskStatusColor = (status: VisitTask['status']) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'skipped': return 'bg-gray-100 text-gray-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'Pending scheduling';
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Convert UTC date string to local datetime-local format (YYYY-MM-DDTHH:mm)
  const toLocalDateTimeString = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const completedTasks = visit ? visit.completed_tasks : tasks.filter(task => task.status === 'completed').length;
  const totalTasks = visit ? visit.total_tasks : tasks.length;
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const isWalkthroughVisit = visit?.visit_type === 'walkthrough';
  const showBookingAttribution =
    visit?.visit_type === 'walkthrough' || visit?.visit_type === 'maintenance';
  const totalChecklistItems = MAINTENANCE_CHECKLIST_TEMPLATE.reduce(
    (count, group) => count + group.items.length,
    0
  );
  const completedChecklistItems = MAINTENANCE_CHECKLIST_TEMPLATE.reduce(
    (count, group) =>
      count + group.items.filter((item) => maintenanceVisitData.checklistStatus[item.id]).length,
    0
  );
  const checklistCompletionRate =
    totalChecklistItems > 0 ? Math.round((completedChecklistItems / totalChecklistItems) * 100) : 0;
  const overdueOrDuePlanCount = walkthroughPlans.filter(
    (p) => p.status === 'overdue' || p.status === 'due'
  ).length;
  const progressStatuses: Array<Visit['status']> = ['requested', 'proposed', 'scheduled', 'in_progress', 'completed'];
  const currentProgressIndex = progressStatuses.indexOf(visit?.status || 'requested');

  if (loading) {
    return (
      <AdminLayout activeTab="visits">
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error && !visit) {
    return (
      <AdminLayout activeTab="visits">
        <div className="p-6">
          <div className="bg-red-50 text-red-700 p-4 rounded-lg">
            {error || 'Visit not found'}
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!visit) {
    return (
      <AdminLayout activeTab="visits">
        <div className="p-6">
          <div className="bg-red-50 text-red-700 p-4 rounded-lg">
            Visit not found
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="visits">
      <div className="h-full overflow-y-auto">
        <div className="p-3 sm:p-6 max-w-6xl mx-auto">
      <div className="mb-6 space-y-3 sm:space-y-4">
        <button
          onClick={() => navigate('/admin?tab=visits')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5 mr-1" />
          Back to Visits
        </button>

        <div>
          <h1 className="text-xl sm:text-2xl font-bold">
            {visit.internal_name || visit.address}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {visit.internal_name && (
              <>
                <MapPin className="w-3.5 h-3.5 inline -mt-0.5 mr-1 text-gray-400" />
                <span>{visit.address}</span>
                <span> · </span>
              </>
            )}
            {formatDateTime(visit.scheduled_date)} · {visit.visit_type.replace('_', ' ')}
          </p>
        </div>

        <div>
          <label
            htmlFor="visit-internal-name"
            className="block text-xs font-medium text-gray-500 mb-1"
          >
            Internal name <span className="font-normal text-gray-400">(admin only)</span>
          </label>
          <div className="flex items-center gap-2">
            <input
              id="visit-internal-name"
              type="text"
              value={internalNameDraft}
              maxLength={120}
              onFocus={() => {
                internalNameFocusedRef.current = true;
              }}
              onChange={(e) => setInternalNameDraft(e.target.value)}
              onBlur={(e) => {
                internalNameFocusedRef.current = false;
                void handleInternalNameSave(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  (e.target as HTMLInputElement).blur();
                }
              }}
              placeholder="e.g. Smith — Furnace tune-up"
              className="w-full sm:max-w-md px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
            />
            {internalNameSaveState === 'saving' && (
              <span className="text-xs text-gray-500">Saving…</span>
            )}
            {internalNameSaveState === 'saved' && (
              <span className="text-xs text-green-600">Saved</span>
            )}
            {internalNameSaveState === 'error' && (
              <span className="text-xs text-red-600">Save failed</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Helps distinguish similar visits in your admin lists. Not shown to homeowners or contractors.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={handleOpenContractorView}
            disabled={generatingContractorLink}
            className="w-full sm:w-auto flex items-center justify-center space-x-2 px-3 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors border border-gray-200 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <ExternalLink className="w-4 h-4" />
            <span>{generatingContractorLink ? 'Generating Link...' : 'Open Contractor View'}</span>
          </button>
          <button
            type="button"
            onClick={handleCopyContractorLink}
            disabled={generatingContractorLink}
            className="w-full sm:w-auto flex items-center justify-center space-x-2 px-3 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors border border-gray-200 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Copy className="w-4 h-4" />
            <span>{generatingContractorLink ? 'Generating Link...' : 'Copy Contractor Link'}</span>
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="w-full sm:w-auto flex items-center justify-center space-x-2 px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors border border-red-100"
          >
            <Trash2 className="w-4 h-4" />
            <span>Delete Visit</span>
          </button>
        </div>
      </div>

      {shareMessage && (
        <div
          className={`mb-6 p-3 rounded-lg text-sm ${
            shareMessageType === 'success'
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-700'
          }`}
        >
          {shareMessage}
        </div>
      )}

      {error && (
        <div className="mb-6 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="divide-y divide-gray-200/90">
      <CollapsibleWidget
        title="Visit Progress"
        open={widgetOpen.visitProgress}
        onToggle={() => toggleWidget('visitProgress')}
        card
      >
        <div className="sm:hidden">
          <select
            value={visit.status}
            onChange={(e) => handleStatusUpdate(e.target.value as Visit['status'])}
            disabled={statusUpdating}
            aria-label="Visit stage"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-50 disabled:text-gray-500"
          >
            {([...progressStatuses, 'cancelled', 'no_show'] as Array<Visit['status']>).map((status) => (
              <option key={status} value={status}>
                {status.replace('_', ' ')}
              </option>
            ))}
          </select>
        </div>

        {/* max-sm:hidden avoids hidden+sm:block order issues where desktop stayed display:none */}
        <div className="max-sm:hidden w-full min-w-0">
          <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-2">
            {progressStatuses.map((status, index) => {
              const isComplete = currentProgressIndex >= index;
              const isCurrent = visit.status === status;
              return (
                <React.Fragment key={status}>
                  <button
                    type="button"
                    onClick={() => handleStatusUpdate(status)}
                    disabled={statusUpdating}
                    className={`min-w-[120px] px-3 py-2 rounded-full text-xs sm:text-sm font-medium border transition-colors ${
                      isCurrent
                        ? 'bg-black text-white border-black'
                        : isComplete
                        ? 'bg-green-50 text-green-800 border-green-200'
                        : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                    } disabled:opacity-50`}
                  >
                    {status.replace('_', ' ')}
                  </button>
                  {index < progressStatuses.length - 1 && (
                    <div className={`h-1 w-8 sm:w-12 rounded ${currentProgressIndex > index ? 'bg-green-300' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleStatusUpdate('cancelled')}
              disabled={statusUpdating}
              className="px-3 py-1.5 text-xs font-medium rounded-md bg-red-50 text-red-700 border border-red-200 disabled:opacity-50"
            >
              Mark Cancelled
            </button>
            <button
              type="button"
              onClick={() => handleStatusUpdate('no_show')}
              disabled={statusUpdating}
              className="px-3 py-1.5 text-xs font-medium rounded-md bg-gray-100 text-gray-700 border border-gray-200 disabled:opacity-50"
            >
              Mark No Show
            </button>
          </div>
        </div>
      </CollapsibleWidget>

      <CollapsibleWidget
        title="Home & Homeowner"
        open={widgetOpen.homeHomeowner}
        onToggle={() => toggleWidget('homeHomeowner')}
      >
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-500">Property</label>
            <div className="font-medium flex items-center">
              <MapPin className="w-4 h-4 mr-1 text-gray-400" />
              {visit.address}
            </div>
            <div className="text-sm text-gray-500">
              {visit.city}, {visit.state} {visit.zip_code}
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-500">Homeowner</label>
            <div className="font-medium flex items-center">
              <User className="w-4 h-4 mr-1 text-gray-400" />
              {visit.owner_name}
            </div>
            {visit.owner_phone && <div className="text-sm text-gray-500">{visit.owner_phone}</div>}
          </div>

          {visit.homeowner_notes && (
            <div className="bg-blue-50 p-3 rounded-lg">
              <label className="text-sm font-medium text-blue-900 flex items-center">
                <MessageSquare className="w-4 h-4 mr-1" />
                Homeowner Notes
              </label>
              <div className="text-sm text-blue-800 mt-1 italic">"{visit.homeowner_notes}"</div>
              {visit.homeowner_submitted_at && (
                <div className="text-xs text-blue-600 mt-1">
                  Submitted {new Date(visit.homeowner_submitted_at).toLocaleDateString()}
                </div>
              )}
            </div>
          )}
        </div>
      </CollapsibleWidget>

      <CollapsibleWidget
        title="Scheduling"
        open={widgetOpen.scheduling}
        onToggle={() => toggleWidget('scheduling')}
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="visit-type-select" className="text-sm text-gray-500">
              Visit Type
            </label>
            <div className="mt-1 flex items-center space-x-2">
              <select
                id="visit-type-select"
                value={visit.visit_type}
                onChange={(e) => handleVisitTypeUpdate(e.target.value as Visit['visit_type'])}
                disabled={visitTypeUpdating}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="walkthrough">Walkthrough</option>
                <option value="maintenance">Maintenance Visit</option>
                <option value="service">Service Visit</option>
              </select>
              {visitTypeUpdating && (
                <span className="text-xs text-gray-500">Updating…</span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Changing the type updates which workflow appears on this visit.
            </p>
          </div>

          <div>
            <label className="text-sm text-gray-500">Start Time</label>
            <div className="mt-1 flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <input
                type="datetime-local"
                value={timeEditMode ? timeDraftScheduledDate : toLocalDateTimeString(visit.scheduled_date)}
                onChange={(e) => setTimeDraftScheduledDate(e.target.value)}
                disabled={!timeEditMode || savingTimeUpdates}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Leave empty for pending scheduling</p>
          </div>

          <div>
            <label className="text-sm text-gray-500">Duration</label>
            <div className="mt-1 flex items-center space-x-2">
              <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <select
                value={
                  timeEditMode
                    ? normalizeVisitDurationMinutes(timeDraftDurationMinutes)
                    : normalizeVisitDurationMinutes(visit.estimated_duration_minutes)
                }
                onChange={(e) => setTimeDraftDurationMinutes(parseInt(e.target.value, 10))}
                disabled={!timeEditMode || savingTimeUpdates}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-50 disabled:text-gray-500"
              >
                {VISIT_DURATION_OPTIONS_MINUTES.map((minutes) => (
                  <option key={minutes} value={minutes}>
                    {formatVisitDurationLabel(minutes)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-500">Assigned To</label>
            <div className="mt-1 flex items-center space-x-2">
              <select
                value={visit.assigned_to ? admins.find(a => a.full_name === visit.assigned_to)?.id || '' : ''}
                onChange={(e) => handleAssignedToUpdate(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
              >
                <option value="">Unassigned</option>
                {admins.map((admin) => (
                  <option key={admin.id} value={admin.id}>
                    {admin.full_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            {!timeEditMode ? (
              <button
                type="button"
                onClick={beginTimeEdit}
                className="w-full px-3 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 transition-colors"
              >
                Edit Time
              </button>
            ) : (
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={cancelTimeEdit}
                  disabled={savingTimeUpdates}
                  className="flex-1 px-3 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveTimeUpdates}
                  disabled={savingTimeUpdates}
                  className="flex-1 px-3 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {savingTimeUpdates ? 'Saving...' : 'Save Time'}
                </button>
              </div>
            )}
          </div>

          {visit.actual_start_time && (
            <div>
              <label className="text-sm text-gray-500">Started (actual)</label>
              <div className="font-medium flex items-center mt-1">
                <Clock className="w-4 h-4 mr-1 text-gray-400" />
                {formatDateTime(visit.actual_start_time)}
              </div>
            </div>
          )}

          {visit.actual_end_time && (
            <div>
              <label className="text-sm text-gray-500">Completed (actual)</label>
              <div className="font-medium flex items-center mt-1">
                <CheckCircle2 className="w-4 h-4 mr-1 text-gray-400" />
                {formatDateTime(visit.actual_end_time)}
              </div>
            </div>
          )}
        </div>
      </CollapsibleWidget>

      <CollapsibleWidget
        title="Provider"
        open={widgetOpen.provider}
        onToggle={() => toggleWidget('provider')}
      >
        <div className="space-y-4">
          <div className="relative">
            <label className="text-sm text-gray-500">Service Provider</label>
            <div className="mt-1 flex items-center space-x-2 min-w-0 relative">
              <div className="flex-1 relative min-w-0">
                <input
                  type="text"
                  value={serviceProviderSearch}
                  onChange={(e) => {
                    setServiceProviderSearch(e.target.value);
                    setShowServiceProviderDropdown(true);
                    setSelectedServiceProviderIndex(-1);
                  }}
                  onFocus={() => setShowServiceProviderDropdown(true)}
                  onBlur={() => {
                    setTimeout(() => setShowServiceProviderDropdown(false), 200);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setSelectedServiceProviderIndex(prev =>
                        prev < filteredServiceProviders.length - 1 ? prev + 1 : prev
                      );
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setSelectedServiceProviderIndex(prev => prev > 0 ? prev - 1 : -1);
                    } else if (e.key === 'Enter' && selectedServiceProviderIndex >= 0) {
                      e.preventDefault();
                      const provider = filteredServiceProviders[selectedServiceProviderIndex];
                      handleServiceProviderUpdate(provider.id);
                    } else if (e.key === 'Escape') {
                      setShowServiceProviderDropdown(false);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black min-w-0"
                  placeholder="Search service providers..."
                />
                {serviceProviderSearch && (
                  <button
                    type="button"
                    onClick={() => {
                      setServiceProviderSearch('');
                      handleServiceProviderUpdate('');
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                {showServiceProviderDropdown && filteredServiceProviders.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    <button
                      type="button"
                      onClick={() => {
                        handleServiceProviderUpdate('');
                        setServiceProviderSearch('');
                      }}
                      className={`w-full text-left px-3 py-2 hover:bg-gray-100 ${
                        selectedServiceProviderIndex === -1 && serviceProviderSearch === ''
                          ? 'bg-gray-100'
                          : ''
                      }`}
                    >
                      <span className="text-gray-500 italic">None (Internal team)</span>
                    </button>
                    {filteredServiceProviders.map((provider, index) => (
                      <button
                        key={provider.id}
                        type="button"
                        onClick={() => handleServiceProviderUpdate(provider.id)}
                        className={`w-full text-left px-3 py-2 hover:bg-gray-100 ${
                          index === selectedServiceProviderIndex ? 'bg-gray-100' : ''
                        }`}
                      >
                        <div className="font-medium">{provider.company_name}</div>
                        {provider.phone && <div className="text-sm text-gray-500">{provider.phone}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-500">Notes for Provider</label>
            <textarea
              value={contractorNotesDraft}
              onChange={(e) => {
                const v = e.target.value;
                setContractorNotesDraft(v);
                scheduleContractorNotesSave(v);
              }}
              onFocus={() => {
                contractorNotesFocusedRef.current = true;
              }}
              onBlur={async (e) => {
                if (contractorNotesDebounceRef.current) {
                  clearTimeout(contractorNotesDebounceRef.current);
                  contractorNotesDebounceRef.current = null;
                }
                await handleContractorNotesUpdate(e.target.value);
                contractorNotesFocusedRef.current = false;
              }}
              rows={3}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
              placeholder="Add notes to share with the provider..."
            />
            <div className="mt-1.5 min-h-[1.25rem] text-xs" aria-live="polite">
              {contractorNotesSaveState === 'saving' && (
                <span className="text-gray-500">Saving provider notes…</span>
              )}
              {contractorNotesSaveState === 'saved' && (
                <span className="text-green-700">Provider notes saved.</span>
              )}
              {contractorNotesSaveState === 'error' && contractorNotesSaveError && (
                <span className="text-red-600">{contractorNotesSaveError}</span>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-500">Provider Notes</label>
            <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700 whitespace-pre-wrap">
              {visit.provider_notes?.trim() ? visit.provider_notes : 'No provider notes yet.'}
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-500">Cost</label>
            <div className="mt-1 flex items-center space-x-2">
              <DollarSign className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <input
                type="number"
                step="0.01"
                value={visit.cost || ''}
                onChange={(e) => handleCostUpdate(e.target.value ? parseFloat(e.target.value) : null)}
                onBlur={(e) => handleCostUpdate(e.target.value ? parseFloat(e.target.value) : null)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="pt-2">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-gray-500">Visit Expenses</label>
              <div className="flex items-center gap-2">
                {selectedExpenseIdsForInvoice.size > 0 && (
                  <button
                    type="button"
                    onClick={handleOpenInvoiceModal}
                    className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
                  >
                    <DollarSign className="w-3.5 h-3.5" />
                    Create Invoice ({selectedExpenseIdsForInvoice.size})
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleOpenExpenseModal()}
                  className="inline-flex items-center gap-1 rounded-md bg-black px-2.5 py-1.5 text-xs font-medium text-white hover:bg-gray-800"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Expense
                </button>
              </div>
            </div>
            {invoiceSuccess && (
              <div className="mb-2 rounded-md bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-800">
                {invoiceSuccess}
              </div>
            )}
            {parentVisitExpenses.length === 0 ? (
              <div className="text-sm text-gray-500 italic">No visit expenses yet.</div>
            ) : (
              <div className="space-y-2">
                {parentVisitExpenses.map((expense) => {
                  const childExpenses = getChildVisitExpenses(expense.id);
                  const isSelectedForInvoice = selectedExpenseIdsForInvoice.has(expense.id);
                  const canInvoice = expense.billable_to_homeowner && !expense.is_paid && !expense.invoice_id;
                  return (
                    <div
                      key={expense.id}
                      className={`rounded-md border bg-gray-50 p-3 ${isSelectedForInvoice ? 'border-indigo-400 ring-1 ring-indigo-300' : 'border-gray-200'}`}
                    >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2">
                        {canInvoice && (
                          <input
                            type="checkbox"
                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 cursor-pointer"
                            checked={isSelectedForInvoice}
                            onChange={(e) => {
                              const next = new Set(selectedExpenseIdsForInvoice);
                              if (e.target.checked) next.add(expense.id);
                              else next.delete(expense.id);
                              setSelectedExpenseIdsForInvoice(next);
                            }}
                          />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-900">
                              ${Number(expense.amount || 0).toFixed(2)}
                            </span>
                            {expense.billable_to_homeowner && (
                              <>
                                {expense.is_paid && (
                                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700">
                                    Paid
                                  </span>
                                )}
                                {!expense.is_paid && expense.invoice_id && (
                                  <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700">
                                    Invoiced
                                  </span>
                                )}
                                {!expense.is_paid && expense.invoice_requested && !expense.invoice_id && (
                                  <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-[10px] font-semibold text-yellow-700">
                                    Needs Invoice
                                  </span>
                                )}
                                {!expense.is_paid && !expense.invoice_id && !expense.invoice_requested && (
                                  <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-600">
                                    Unpaid
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                          <div className="text-sm text-gray-700">
                            {expense.description || expense.category || 'Visit expense'}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {expense.expense_date || 'No date'}{expense.vendor ? ` • ${expense.vendor}` : ''}
                          </div>
                          {childExpenses.length > 0 && (
                            <div className="text-xs text-gray-500 mt-1">
                              {childExpenses.length} line item{childExpenses.length === 1 ? '' : 's'}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleOpenExpenseModal(undefined, expense)}
                          className="text-xs text-gray-700 hover:text-gray-900"
                        >
                          Add Line Item
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenExpenseModal(expense)}
                          className="text-xs text-blue-700 hover:text-blue-900"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteVisitExpense(expense.id)}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    {childExpenses.length > 0 && (
                      <div className="mt-3 space-y-2 border-t border-gray-200 pt-2">
                        {childExpenses.map((lineItem) => (
                          <div key={lineItem.id} className="flex items-center justify-between rounded bg-white px-2 py-1.5">
                            <div>
                              <div className="text-xs font-medium text-gray-800">
                                {lineItem.description || lineItem.category || 'Line item'}
                              </div>
                              <div className="text-[11px] text-gray-500">
                                {lineItem.homeowner_visible ? 'Visible to homeowner' : 'Internal only'}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-700">
                                ${Number(lineItem.amount || 0).toFixed(2)}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleOpenExpenseModal(lineItem)}
                                className="text-[11px] text-blue-700 hover:text-blue-900"
                              >
                                Edit
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </CollapsibleWidget>

      {/* Internal Notes */}
      <CollapsibleWidget
        title="Internal Notes"
        open={widgetOpen.internalNotes}
        onToggle={() => toggleWidget('internalNotes')}
      >
        {showBookingAttribution && (
          <div className="mb-4 space-y-3 pb-4 border-b border-gray-200">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Booking attribution
            </p>
            <p className="text-xs text-gray-500 -mt-1">
              Who scheduled this visit and how it was booked. Used for performance reporting (separate
              from assigned visit lead).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label
                  className="block text-sm text-gray-600 mb-1"
                  htmlFor="visit-detail-booked-by"
                >
                  Booked by
                </label>
                <select
                  id="visit-detail-booked-by"
                  value={visit.booked_by || ''}
                  onChange={(e) =>
                    void handleBookingAttributionUpdate({
                      booked_by: e.target.value || null,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black text-sm bg-white"
                >
                  <option value="">Not set</option>
                  {admins.map((admin) => (
                    <option key={admin.id} value={admin.id}>
                      {admin.full_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  className="block text-sm text-gray-600 mb-1"
                  htmlFor="visit-detail-booking-source"
                >
                  Booking source
                </label>
                <select
                  id="visit-detail-booking-source"
                  value={visit.booking_source || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    void handleBookingAttributionUpdate({
                      booking_source:
                        v === ''
                          ? null
                          : isBookingSource(v)
                            ? v
                            : null,
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black text-sm bg-white"
                >
                  <option value="">Not set</option>
                  {BOOKING_SOURCES.map((source) => (
                    <option key={source} value={source}>
                      {BOOKING_SOURCE_LABELS[source]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
        <textarea
          value={visit.visit_notes || ''}
          onChange={(e) => handleInternalNotesUpdate(e.target.value)}
          onBlur={(e) => handleInternalNotesUpdate(e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
          placeholder="Internal notes about this visit..."
        />
      </CollapsibleWidget>

      {isWalkthroughVisit && (
        <CollapsibleWidget
          title="Walkthrough Workflow"
          open={widgetOpen.maintenanceWorkflow}
          onToggle={() => toggleWidget('maintenanceWorkflow')}
        >
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs uppercase tracking-wide text-gray-500">Checklist</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900">
                  {completedChecklistItems}/{totalChecklistItems}
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs uppercase tracking-wide text-gray-500">Completion</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900">{checklistCompletionRate}%</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs uppercase tracking-wide text-gray-500">Maintenance Plans</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900">
                  {walkthroughPlansLoading ? '…' : walkthroughPlans.length}
                  {overdueOrDuePlanCount > 0 && (
                    <span className="ml-2 text-sm font-medium text-red-600">
                      {overdueOrDuePlanCount} due
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-green-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${checklistCompletionRate}%` }}
              />
            </div>

            <div className="space-y-5">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600">
                Standard Procedure Checklist
              </h3>
              {MAINTENANCE_CHECKLIST_TEMPLATE.map((group) => (
                <div key={group.id} className="rounded-lg border border-gray-200">
                  <div className="border-b border-gray-200 px-4 py-3">
                    <h4 className="text-sm font-semibold text-gray-800">{group.title}</h4>
                  </div>
                  <div className="space-y-2 p-3">
                    {group.id === 'systems-walkthrough' && (
                      <button
                        type="button"
                        onClick={() => setShowConfigureHomeSystemsModal(true)}
                        className="w-full rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-left transition-colors min-h-[52px] hover:bg-blue-100"
                      >
                        <span className="flex items-center justify-between gap-3">
                          <span className="font-medium text-blue-900">Configure home systems</span>
                          <span className="inline-flex items-center rounded-full bg-blue-600 px-2.5 py-1 text-xs font-medium text-white">
                            Open
                          </span>
                        </span>
                      </button>
                    )}
                    {group.items.map((item) => {
                      const checked = maintenanceVisitData.checklistStatus[item.id] ?? false;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => toggleMaintenanceChecklistItem(item.id)}
                          className={`w-full rounded-lg border px-4 py-3 text-left transition-colors min-h-[52px] ${
                            checked
                              ? 'border-green-300 bg-green-50 text-green-900'
                              : 'border-gray-200 bg-white text-gray-800 hover:bg-gray-50'
                          }`}
                        >
                          <span className="flex items-center justify-between gap-3">
                            <span className="font-medium">{item.label}</span>
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                                checked ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {checked ? 'Done' : 'Tap to complete'}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600">
                Maintenance Plans
              </h3>
              {walkthroughPlansLoading ? (
                <div className="py-6 text-center text-sm text-gray-500">Loading plans…</div>
              ) : walkthroughPlans.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center">
                  <p className="text-sm font-medium text-gray-700">No maintenance plans configured</p>
                  <p className="mt-1 text-sm text-gray-500">
                    Use the "Configure home systems" button above to set up this home's systems and generate its maintenance schedule.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {walkthroughPlans.map((plan) => {
                    const name = plan.is_custom ? plan.custom_task_name : plan.maintenance_tasks?.name;
                    const description = plan.is_custom ? plan.custom_description : plan.maintenance_tasks?.description;
                    const frequencyMonths =
                      plan.custom_frequency_months != null
                        ? Number(plan.custom_frequency_months)
                        : plan.maintenance_tasks?.frequency_months != null
                        ? Number(plan.maintenance_tasks.frequency_months)
                        : null;

                    const dueDate = plan.next_due_date ? new Date(plan.next_due_date) : null;
                    const today = new Date();
                    const daysUntilDue = dueDate
                      ? Math.floor((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
                      : null;

                    const statusConfig = (() => {
                      if (plan.status === 'overdue' || (daysUntilDue !== null && daysUntilDue < 0)) {
                        return { label: 'Overdue', className: 'bg-red-100 text-red-700' };
                      }
                      if (plan.status === 'due' || (daysUntilDue !== null && daysUntilDue <= 30)) {
                        return { label: 'Due soon', className: 'bg-amber-100 text-amber-700' };
                      }
                      if (plan.status === 'completed') {
                        return { label: 'Completed', className: 'bg-green-100 text-green-700' };
                      }
                      return { label: 'Upcoming', className: 'bg-gray-100 text-gray-600' };
                    })();

                    return (
                      <div key={plan.id} className="rounded-lg border border-gray-200 p-4 space-y-3">
                        {/* Header row: name + status + due date */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium text-gray-900">{name}</span>
                              {plan.is_custom && (
                                <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                                  Custom
                                </span>
                              )}
                              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusConfig.className}`}>
                                {statusConfig.label}
                              </span>
                            </div>
                            {description && (
                              <p className="mt-1 text-sm text-gray-500">{description}</p>
                            )}
                          </div>
                          {dueDate && (
                            <div className="shrink-0 text-right text-xs text-gray-500">
                              {daysUntilDue !== null && daysUntilDue < 0
                                ? `${Math.abs(daysUntilDue)}d overdue`
                                : daysUntilDue === 0
                                ? 'Due today'
                                : `Due ${dueDate.toLocaleDateString()}`}
                            </div>
                          )}
                        </div>

                        {/* Frequency stepper */}
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-500">Every</span>
                          <div className="flex items-center rounded-xl border border-gray-200 bg-gray-50 overflow-hidden">
                            <button
                              type="button"
                              disabled={!frequencyMonths || frequencyMonths <= 1}
                              onClick={() =>
                                frequencyMonths && frequencyMonths > 1 &&
                                handlePlanFrequencyUpdate(plan.id, frequencyMonths - 1)
                              }
                              className="flex items-center justify-center w-11 h-11 text-xl font-medium text-gray-600 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors select-none"
                            >
                              −
                            </button>
                            <div className="flex flex-col items-center justify-center min-w-[56px] px-2 border-x border-gray-200">
                              <span className="text-lg font-semibold text-gray-900 leading-tight">
                                {frequencyMonths ?? '—'}
                              </span>
                              <span className="text-xs text-gray-400 leading-tight">
                                {frequencyMonths === 1 ? 'month' : 'months'}
                              </span>
                            </div>
                            <button
                              type="button"
                              disabled={!frequencyMonths || frequencyMonths >= 12}
                              onClick={() =>
                                frequencyMonths && frequencyMonths < 12 &&
                                handlePlanFrequencyUpdate(plan.id, frequencyMonths + 1)
                              }
                              className="flex items-center justify-center w-11 h-11 text-xl font-medium text-gray-600 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors select-none"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Add custom task inline form */}
                  {showAddCustomTask ? (
                    <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-4 space-y-4">
                      <p className="text-sm font-semibold text-blue-900">New custom maintenance task</p>

                      {/* Task name */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Task name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={newTaskName}
                          onChange={(e) => setNewTaskName(e.target.value)}
                          placeholder="e.g. Clean dryer vent"
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>

                      {/* Description */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Description <span className="text-gray-400">(optional)</span>
                        </label>
                        <textarea
                          value={newTaskDescription}
                          onChange={(e) => setNewTaskDescription(e.target.value)}
                          placeholder="Any notes or instructions…"
                          rows={2}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                        />
                      </div>

                      {/* Frequency stepper */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-2">Frequency</label>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-500">Every</span>
                          <div className="flex items-center rounded-xl border border-gray-200 bg-white overflow-hidden">
                            <button
                              type="button"
                              disabled={newTaskFrequency <= 1}
                              onClick={() => setNewTaskFrequency((f) => Math.max(1, f - 1))}
                              className="flex items-center justify-center w-11 h-11 text-xl font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors select-none"
                            >
                              −
                            </button>
                            <div className="flex flex-col items-center justify-center min-w-[56px] px-2 border-x border-gray-200">
                              <span className="text-lg font-semibold text-gray-900 leading-tight">
                                {newTaskFrequency}
                              </span>
                              <span className="text-xs text-gray-400 leading-tight">
                                {newTaskFrequency === 1 ? 'month' : 'months'}
                              </span>
                            </div>
                            <button
                              type="button"
                              disabled={newTaskFrequency >= 12}
                              onClick={() => setNewTaskFrequency((f) => Math.min(12, f + 1))}
                              className="flex items-center justify-center w-11 h-11 text-xl font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors select-none"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 pt-1">
                        <button
                          type="button"
                          onClick={handleAddCustomTask}
                          disabled={!newTaskName.trim() || savingCustomTask}
                          className="flex-1 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {savingCustomTask ? 'Saving…' : 'Add task'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowAddCustomTask(false);
                            setNewTaskName('');
                            setNewTaskDescription('');
                            setNewTaskFrequency(1);
                          }}
                          className="rounded-xl bg-white border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowAddCustomTask(true)}
                      className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 py-3 text-sm font-medium text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
                    >
                      <span className="text-lg leading-none">+</span>
                      Add custom task
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* ── Next Steps ── */}
            {(() => {
              const hasVisits = scheduledMaintenanceVisits.length > 0;
              const visitStatusConfig: Record<string, { label: string; className: string }> = {
                scheduled:   { label: 'Scheduled',   className: 'bg-blue-100 text-blue-700' },
                proposed:    { label: 'Proposed',    className: 'bg-yellow-100 text-yellow-700' },
                requested:   { label: 'Requested',   className: 'bg-gray-100 text-gray-600' },
                in_progress: { label: 'In Progress', className: 'bg-orange-100 text-orange-700' },
                completed:   { label: 'Completed',   className: 'bg-green-100 text-green-700' },
              };
              return (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Next Steps
                  </h4>
                  <div className="space-y-2">
                    {/* Step card */}
                    <div className={`rounded-xl border transition-colors ${hasVisits ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}>
                      {/* Step header row */}
                      <div className="flex items-center justify-between gap-3 p-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${hasVisits ? 'border-green-500 bg-green-500' : 'border-gray-300'}`}>
                            {hasVisits && (
                              <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className={`text-sm font-medium ${hasVisits ? 'text-green-800' : 'text-gray-900'}`}>
                              Schedule first maintenance visit
                            </p>
                            {hasVisits && (
                              <p className="text-xs text-green-600 mt-0.5">
                                {scheduledMaintenanceVisits.length} maintenance visit{scheduledMaintenanceVisits.length > 1 ? 's' : ''} scheduled
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowScheduleVisitModal(true)}
                          className={`flex-shrink-0 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
                            hasVisits
                              ? 'border border-green-300 bg-white text-green-800 hover:bg-green-100'
                              : 'bg-black text-white hover:bg-gray-800'
                          }`}
                        >
                          {hasVisits ? '+ Add another' : 'Schedule'}
                        </button>
                      </div>

                      {/* Scheduled visits list */}
                      {hasVisits && (
                        <div className="border-t border-green-200 divide-y divide-green-100">
                          {scheduledMaintenanceVisits.map((mv) => {
                            const mvDate = mv.scheduled_date ? new Date(mv.scheduled_date) : null;
                            const statusCfg = visitStatusConfig[mv.status] ?? { label: mv.status, className: 'bg-gray-100 text-gray-600' };
                            return (
                              <div key={mv.id} className="flex items-center justify-between gap-3 px-4 py-3">
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-sm font-medium text-gray-900">
                                      {mvDate
                                        ? mvDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                                        : 'Date TBD'}
                                    </span>
                                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusCfg.className}`}>
                                      {statusCfg.label}
                                    </span>
                                  </div>
                                  <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-gray-500">
                                    {mvDate && (
                                      <span>
                                        {mvDate.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                                      </span>
                                    )}
                                    {mv.estimated_duration_minutes && (
                                      <span>{formatVisitDurationLabel(mv.estimated_duration_minutes)}</span>
                                    )}
                                    {mv.assigned_to && <span>Assigned: {mv.assigned_to}</span>}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => window.open(`/admin/visit?id=${mv.id}`, '_blank')}
                                  className="flex-shrink-0 flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                  Open
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="min-h-[1.25rem] text-xs" aria-live="polite">
              {maintenanceDataSaveState === 'saving' && (
                <span className="text-gray-500">Saving walkthrough workflow changes...</span>
              )}
              {maintenanceDataSaveState === 'saved' && (
                <span className="text-green-700">Walkthrough workflow saved.</span>
              )}
              {maintenanceDataSaveState === 'error' && maintenanceDataSaveError && (
                <span className="text-red-600">{maintenanceDataSaveError}</span>
              )}
            </div>
          </div>
        </CollapsibleWidget>
      )}

      {/* Tasks - Hidden for walkthrough visits */}
      {visit.visit_type !== 'walkthrough' && (
        <CollapsibleWidget
          title="Tasks"
          open={widgetOpen.tasks}
          onToggle={() => toggleWidget('tasks')}
          headerRight={
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowAddTaskModal(true);
                fetchServiceRequests();
                fetchMaintenanceItems();
              }}
              className="flex shrink-0 items-center space-x-2 px-3 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Task</span>
            </button>
          }
        >
            <div className="mb-6 pb-6 border-b border-gray-100">
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Total</div>
                  <div className="text-lg font-semibold">{totalTasks}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Completed</div>
                  <div className="text-lg font-semibold text-green-600">{completedTasks}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Progress</div>
                  <div className="text-lg font-semibold">{completionRate}%</div>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-green-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${completionRate}%` }}
                />
              </div>
            </div>

            <div className="space-y-4">
              {tasks.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Tasks Yet</h3>
                  <p className="text-gray-600 mb-4">Add tasks to track what needs to be done during this visit.</p>
                  <button
                    onClick={() => {
                      setShowAddTaskModal(true);
                      fetchServiceRequests();
                      fetchMaintenanceItems();
                    }}
                    className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                  >
                    Add First Task
                  </button>
                </div>
              ) : (
                tasks.map((task) => (
                  <div key={task.id} className={`border rounded-lg p-4 ${task.requested_by_homeowner ? 'border-blue-200 bg-blue-50' : ''}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-medium text-gray-900">{task.task_title}</h3>
                          {task.requested_by_homeowner && (
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-blue-600 text-white">
                              <User className="w-3 h-3 mr-1" />
                              Homeowner Request
                            </span>
                          )}
                          {task.homeowner_priority && (
                            <span className="text-xs text-blue-600">
                              Priority: {task.homeowner_priority}
                            </span>
                          )}
                        </div>
                        {task.task_description && (
                          <p className="text-sm text-gray-600 mt-1">{task.task_description}</p>
                        )}
                        {task.homeowner_notes && (
                          <div className="mt-2 text-sm text-blue-700 italic bg-blue-100 p-2 rounded">
                            <MessageSquare className="w-3 h-3 inline mr-1" />
                            Homeowner: "{task.homeowner_notes}"
                          </div>
                        )}
                      </div>
                      <div className="flex space-x-2 ml-4">
                        {task.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleTaskStatusUpdate(task.id, 'completed')}
                              className="text-green-600 hover:text-green-800"
                              title="Mark as completed"
                            >
                              <CheckCircle2 className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleTaskStatusUpdate(task.id, 'skipped')}
                              className="text-gray-600 hover:text-gray-800"
                              title="Skip task"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setTaskToDelete(task.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete task"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs ${getTaskStatusColor(task.status)}`}>
                        {task.status}
                      </span>
                      {task.task_type === 'service_request' && task.task_id ? (
                        <button
                          onClick={() => navigate(`/admin/service-request/${task.task_id}`)}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors"
                        >
                          {task.task_type.replace('_', ' ')}
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </button>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-purple-100 text-purple-800">
                          {task.task_type.replace('_', ' ')}
                        </span>
                      )}
                      {task.estimated_duration_minutes && (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-gray-100 text-gray-800">
                          {task.estimated_duration_minutes}min est.
                        </span>
                      )}
                      {task.actual_duration_minutes && (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-green-100 text-green-800">
                          {task.actual_duration_minutes}min actual
                        </span>
                      )}
                    </div>

                    {task.notes && (
                      <div className="mt-2 text-sm text-gray-600 italic">
                        Internal Note: {task.notes}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
        </CollapsibleWidget>
      )}

      <CollapsibleWidget
        title="Photos"
        open={widgetOpen.photos}
        onToggle={() => toggleWidget('photos')}
        headerRight={
          <label
            className={`flex shrink-0 items-center space-x-2 px-3 py-2 rounded-md transition-colors cursor-pointer ${
              uploadingPhoto
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {uploadingPhoto ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                <span>Upload Photo</span>
              </>
            )}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhotoUpload}
              disabled={uploadingPhoto}
            />
          </label>
        }
      >
            <div className="mb-4 flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Photo Type</label>
                <select
                  value={photoType}
                  onChange={(e) => setPhotoType(e.target.value as typeof photoType)}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
                  disabled={uploadingPhoto}
                >
                  <option value="general">General</option>
                  <option value="before">Before</option>
                  <option value="during">During</option>
                  <option value="after">After</option>
                  <option value="issue">Issue</option>
                  <option value="completion">Completion</option>
                </select>
              </div>
              <div className="flex-1 min-w-48">
                <label className="block text-xs font-medium text-gray-600 mb-1">Description (optional)</label>
                <input
                  type="text"
                  value={photoDescription}
                  onChange={(e) => setPhotoDescription(e.target.value)}
                  placeholder="Add a description..."
                  className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
                  disabled={uploadingPhoto}
                />
              </div>
            </div>
            {photoUploadMessage && (
              <p className={`mb-3 text-sm ${photoUploadMessageType === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                {photoUploadMessage}
              </p>
            )}
            {photos.length === 0 ? (
              <div className="text-center py-8">
                <Camera className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Photos Yet</h3>
                <p className="text-gray-600">Upload photos using the button above.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {photos.map((photo) => (
                  <div key={photo.id} className="relative">
                    <img
                      src={photo.photo_url}
                      alt={photo.description || 'Visit photo'}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <div className="absolute bottom-2 left-2">
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-black bg-opacity-75 text-white">
                        {photo.photo_type}
                      </span>
                    </div>
                    {photo.description && (
                      <div className="mt-1 text-sm text-gray-600 truncate">
                        {photo.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
      </CollapsibleWidget>
      </div>

      {/* Add Task Modal - Hidden for walkthrough visits */}
      {showAddTaskModal && visit.visit_type !== 'walkthrough' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add Task</h3>
              <button
                onClick={() => {
                  setShowAddTaskModal(false);
                  setSelectedServiceRequestId(null);
                  setSelectedMaintenanceItemId(null);
                  setNewTask({
                    task_title: '',
                    task_description: '',
                    task_type: 'user_task',
                    estimated_duration_minutes: 60,
                    task_id: null
                  });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddTask} className="space-y-4">
              {/* Task Source Selection */}
              {visit?.home_id && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Task Source
                  </label>
                  {(loadingServiceRequests || loadingMaintenanceItems) ? (
                    <div className="text-sm text-gray-500 py-2">Loading items...</div>
                  ) : (
                    <div className="space-y-2 border border-gray-300 rounded-md p-2 max-h-96 overflow-y-auto">
                      {/* Create New Task Option */}
                      <label className="flex items-start cursor-pointer hover:bg-gray-50 rounded-md p-2 -m-2">
                        <input
                          type="radio"
                          name="taskSource"
                          checked={selectedServiceRequestId === null && selectedMaintenanceItemId === null}
                          onChange={() => {
                            handleServiceRequestSelect(null);
                            handleMaintenanceItemSelect(null);
                          }}
                          className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">Create New Task</div>
                          <div className="text-xs text-gray-500 mt-0.5">Manually enter task details</div>
                        </div>
                      </label>

                      {/* Service Requests Section */}
                      {serviceRequests.length > 0 && (
                        <>
                          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-2 pb-1 px-2">
                            Service Requests
                          </div>
                          {serviceRequests.map((sr) => (
                            <label
                              key={sr.id}
                              className="flex items-start cursor-pointer hover:bg-gray-50 rounded-md p-2 -m-2"
                            >
                              <input
                                type="radio"
                                name="taskSource"
                                checked={selectedServiceRequestId === sr.id}
                                onChange={() => handleServiceRequestSelect(sr.id)}
                                className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">{sr.title}</div>
                                {sr.description && (
                                  <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                                    {sr.description}
                                  </div>
                                )}
                                <div className="flex items-center space-x-2 mt-1">
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    sr.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                                    sr.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                                    sr.priority === 'normal' ? 'bg-blue-100 text-blue-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {sr.priority}
                                  </span>
                                  <span className="text-xs text-gray-500">{sr.status}</span>
                                </div>
                              </div>
                            </label>
                          ))}
                        </>
                      )}

                      {/* Maintenance Items Section */}
                      {maintenanceItems.length > 0 && (
                        <>
                          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-2 pb-1 px-2">
                            Maintenance Items
                          </div>
                          {maintenanceItems.map((item) => (
                            <label
                              key={item.id}
                              className="flex items-start cursor-pointer hover:bg-gray-50 rounded-md p-2 -m-2"
                            >
                              <input
                                type="radio"
                                name="taskSource"
                                checked={selectedMaintenanceItemId === item.id}
                                onChange={() => handleMaintenanceItemSelect(item.id)}
                                className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                {item.description && (
                                  <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                                    {item.description}
                                  </div>
                                )}
                                <div className="flex items-center space-x-2 mt-1">
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    item.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                                    item.priority === 'normal' ? 'bg-blue-100 text-blue-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {item.priority}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    Due: {new Date(item.next_due_date).toLocaleDateString()}
                                  </span>
                                  <span className="text-xs text-gray-500">{item.status}</span>
                                </div>
                              </div>
                            </label>
                          ))}
                        </>
                      )}

                      {/* No items message */}
                      {serviceRequests.length === 0 && maintenanceItems.length === 0 && (
                        <div className="text-sm text-gray-500 py-2 px-3">
                          No open service requests or maintenance items for this home
                        </div>
                      )}
                    </div>
                  )}
                  {(selectedServiceRequestId || selectedMaintenanceItemId) && (
                    <div className="mt-2 text-xs text-blue-600">
                      Selected item will be linked to this visit task
                    </div>
                  )}
                </div>
              )}
              {(selectedServiceRequestId || selectedMaintenanceItemId) && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="text-sm font-medium text-blue-900">
                    Linked to {selectedServiceRequestId ? 'Service Request' : 'Maintenance Item'}
                  </div>
                  <div className="text-xs text-blue-700 mt-1">
                    Task details are populated from the selected item
                  </div>
                </div>
              )}

              {!(selectedServiceRequestId || selectedMaintenanceItemId) && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Task Title
                    </label>
                    <input
                      type="text"
                      value={newTask.task_title}
                      onChange={(e) => setNewTask({ ...newTask, task_title: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                      placeholder="What needs to be done?"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={newTask.task_description}
                      onChange={(e) => setNewTask({ ...newTask, task_description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                      placeholder="Additional details..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Task Type
                    </label>
                    <select
                      value={newTask.task_type}
                      onChange={(e) => setNewTask({ ...newTask, task_type: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                    >
                      <option value="user_task">User Task</option>
                      <option value="maintenance_task">Maintenance Task</option>
                      <option value="service_request">Service Request</option>
                    </select>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estimated Duration (minutes)
                </label>
                <input
                  type="number"
                  value={newTask.estimated_duration_minutes}
                  onChange={(e) => setNewTask({ ...newTask, estimated_duration_minutes: parseInt(e.target.value) })}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddTaskModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingTask}
                  className="flex-1 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {addingTask ? 'Adding...' : 'Add Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-start space-x-4 mb-4">
              <div className="flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Visit</h3>
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Are you sure you want to delete this visit? This action cannot be undone.
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                    <p className="text-sm text-amber-800 font-medium mb-1">Important:</p>
                    <p className="text-sm text-amber-700">
                      This should only be used if the visit was created in error. If the homeowner cancelled the visit, 
                      please change the status to "Cancelled" instead of deleting it.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteVisit}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete Visit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Configure Home Systems modal – only mounted for walkthrough visits */}
      {isWalkthroughVisit && visit && (
        <ConfigureHomeSystemsModal
          homeId={visit.home_id}
          isOpen={showConfigureHomeSystemsModal}
          onClose={() => setShowConfigureHomeSystemsModal(false)}
          onSaved={() => {
            setShowConfigureHomeSystemsModal(false);
            void fetchWalkthroughPlans(visit.home_id);
          }}
        />
      )}

      {/* Schedule Visit modal – for Next Steps */}
      {isWalkthroughVisit && visit && (
        <ScheduleVisitModal
          homeId={visit.home_id}
          homeAddress={visit.address}
          ownerName={visit.owner_name ?? undefined}
          isOpen={showScheduleVisitModal}
          defaultVisitType="maintenance"
          onClose={() => setShowScheduleVisitModal(false)}
          onScheduled={() => {
            setShowScheduleVisitModal(false);
            void fetchScheduledMaintenanceVisits(visit.home_id, visit.id);
          }}
        />
      )}

      {showExpenseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-3xl rounded-lg bg-white p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {editingExpense
                  ? editingExpense.parent_expense_id
                    ? 'Edit Line Item'
                    : 'Edit Visit Expense'
                  : lineItemParentExpense
                    ? 'Add Line Item'
                    : 'Add Visit Expense'}
              </h3>
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-gray-500">
                  {expenseWizardStep === 1 && 'Step 1 of 4 - Overview'}
                  {expenseWizardStep === 2 && 'Step 2 of 4 - Details'}
                  {expenseWizardStep === 3 && 'Step 3 of 4 - Costs'}
                  {expenseWizardStep === 4 && 'Step 4 of 4 - Review'}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setShowExpenseModal(false);
                    resetExpenseForm();
                  }}
                  className="text-gray-400 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="mb-5 grid grid-cols-4 gap-2">
              {[1, 2, 3, 4].map((step) => (
                <div
                  key={step}
                  className={`h-1.5 rounded-full ${expenseWizardStep >= step ? 'bg-black' : 'bg-gray-200'}`}
                />
              ))}
            </div>

            <div className="max-h-[62vh] overflow-y-auto pr-1">
              {expenseWizardStep === 1 && (
                <div className="space-y-4">
                  {isLineItemFlow ? (
                    <>
                      <div className="rounded-md border border-blue-100 bg-blue-50 p-4">
                        <p className="text-sm font-medium text-blue-900">Adding a single line item</p>
                        <p className="mt-1 text-sm text-blue-800">
                          This is a cost component under an existing parent expense. It can be visible to homeowners or internal-only.
                        </p>
                      </div>
                      {lineItemParentExpense && (
                        <div className="rounded-md border border-gray-200 p-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Parent expense</p>
                          <p className="mt-1 text-lg font-semibold text-gray-900">
                            ${Number(lineItemParentExpense.amount || 0).toFixed(2)}
                          </p>
                          {lineItemParentExpense.description && (
                            <p className="mt-1 text-sm text-gray-600">{lineItemParentExpense.description}</p>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="rounded-md border border-blue-100 bg-blue-50 p-4">
                      <p className="text-sm font-medium text-blue-900">Build up an expense from cost components.</p>
                      <p className="mt-1 text-sm text-blue-800">
                        Add labor, materials, fees, and parts as individual line items. Totals and visibility behave like the service request wizard.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {expenseWizardStep === 2 && (
                <div className="space-y-4">
                  {isNewParentFlow && (
                    <div className="rounded-md border border-gray-200 p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900">Add cost components</p>
                        <button
                          type="button"
                          onClick={() => setShowWizardAddLineItemForm((prev) => !prev)}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          {showWizardAddLineItemForm ? 'Hide form' : 'Add line item'}
                        </button>
                      </div>
                      {wizardLineItems.length > 0 && (
                        <div className="mb-3 space-y-2">
                          {wizardLineItems.map((item, idx) => (
                            <div key={item.tempId} className="flex items-center justify-between rounded-md bg-gray-50 p-2">
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {item.description || item.category || 'Line item'}
                                </p>
                                <div className="mt-1 flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setWizardLineItems((prev) =>
                                        prev.map((existing, existingIdx) =>
                                          existingIdx === idx
                                            ? { ...existing, homeowner_visible: !existing.homeowner_visible }
                                            : existing
                                        )
                                      )
                                    }
                                    className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                      item.homeowner_visible
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'bg-gray-200 text-gray-700'
                                    }`}
                                  >
                                    {item.homeowner_visible ? 'Visible' : 'Hidden'}
                                  </button>
                                  <p className="text-xs text-gray-500">
                                    ${Number(item.amount || 0).toFixed(2)}
                                    {item.internal_cost ? ` (cost $${Number(item.internal_cost).toFixed(2)})` : ''}
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  setWizardLineItems((prev) =>
                                    prev.filter((existing) => existing.tempId !== item.tempId)
                                  )
                                }
                                className="rounded-md p-1 text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {showWizardAddLineItemForm && (
                        <div className="grid grid-cols-1 gap-3 rounded-md border border-gray-200 p-3 md:grid-cols-2">
                          <input
                            value={wizardLineItemDraft.description}
                            onChange={(e) =>
                              setWizardLineItemDraft((prev) => ({ ...prev, description: e.target.value }))
                            }
                            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                            placeholder="Line item description"
                          />
                          <input
                            value={wizardLineItemDraft.category}
                            onChange={(e) =>
                              setWizardLineItemDraft((prev) => ({ ...prev, category: e.target.value }))
                            }
                            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                            placeholder="Category"
                          />
                          <input
                            value={wizardLineItemDraft.vendor}
                            onChange={(e) =>
                              setWizardLineItemDraft((prev) => ({ ...prev, vendor: e.target.value }))
                            }
                            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                            placeholder="Vendor"
                          />
                          <input
                            type="number"
                            step="0.01"
                            value={wizardLineItemDraft.amount}
                            onChange={(e) =>
                              setWizardLineItemDraft((prev) => ({ ...prev, amount: e.target.value }))
                            }
                            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                            placeholder="Amount"
                          />
                          <input
                            type="number"
                            step="0.01"
                            value={wizardLineItemDraft.internal_cost}
                            onChange={(e) =>
                              setWizardLineItemDraft((prev) => ({ ...prev, internal_cost: e.target.value }))
                            }
                            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                            placeholder="Internal cost (optional)"
                          />
                          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={wizardLineItemDraft.homeowner_visible}
                              onChange={(e) =>
                                setWizardLineItemDraft((prev) => ({
                                  ...prev,
                                  homeowner_visible: e.target.checked,
                                }))
                              }
                            />
                            Show to homeowner
                          </label>
                          <div className="md:col-span-2">
                            <button
                              type="button"
                              onClick={() => {
                                const parsedLineAmount = parseFloat(wizardLineItemDraft.amount);
                                if (Number.isNaN(parsedLineAmount) || parsedLineAmount <= 0) {
                                  setError('Line item amount must be greater than zero.');
                                  return;
                                }
                                setWizardLineItems((prev) => [
                                  ...prev,
                                  { ...wizardLineItemDraft, tempId: `${Date.now()}-${Math.random()}` },
                                ]);
                                setWizardLineItemDraft(getDefaultVisitWizardLineItemDraft());
                                setShowWizardAddLineItemForm(false);
                              }}
                              className="rounded-md bg-black px-3 py-2 text-sm font-medium text-white hover:bg-gray-800"
                            >
                              Add line item
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {!isNewParentFlow && (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="md:col-span-2">
                        <label className="text-sm text-gray-600">Description</label>
                        <input
                          value={expenseFormData.description}
                          onChange={(e) => setExpenseFormData((prev) => ({ ...prev, description: e.target.value }))}
                          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          placeholder={isLineItemFlow ? 'e.g. Contractor labor' : 'e.g. Gutter cleaning service'}
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Date</label>
                        <input
                          type="date"
                          value={expenseFormData.expense_date}
                          onChange={(e) => setExpenseFormData((prev) => ({ ...prev, expense_date: e.target.value }))}
                          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Category</label>
                        <input
                          value={expenseFormData.category}
                          onChange={(e) => setExpenseFormData((prev) => ({ ...prev, category: e.target.value }))}
                          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          placeholder="Labor, Materials, Parts..."
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-sm text-gray-600">Vendor / Payee</label>
                        <input
                          value={expenseFormData.vendor}
                          onChange={(e) => setExpenseFormData((prev) => ({ ...prev, vendor: e.target.value }))}
                          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          placeholder="Vendor name"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {expenseWizardStep === 3 && (
                <div className="space-y-4">
                  {isNewParentFlow ? (
                    <div className="rounded-md border border-gray-200 p-4">
                      <p className="text-sm font-medium text-gray-900">Computed total from line items</p>
                      <p className="mt-1 text-2xl font-semibold text-gray-900">
                        ${computedWizardLineItemsTotal.toFixed(2)}
                      </p>
                      {computedWizardInternalTotal > 0 && (
                        <p className="mt-1 text-xs text-gray-600">
                          Internal cost: ${computedWizardInternalTotal.toFixed(2)} · Margin: $
                          {(computedWizardLineItemsTotal - computedWizardInternalTotal).toFixed(2)}
                        </p>
                      )}
                      <label className="mt-3 inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={wizardAmountOverride}
                          onChange={(e) => setWizardAmountOverride(e.target.checked)}
                        />
                        Override computed total
                      </label>
                      {wizardAmountOverride && (
                        <div className="mt-3">
                          <label className="text-sm text-gray-600">Override amount</label>
                          <input
                            type="number"
                            step="0.01"
                            value={expenseFormData.amount}
                            onChange={(e) => setExpenseFormData((prev) => ({ ...prev, amount: e.target.value }))}
                            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            placeholder="0.00"
                          />
                        </div>
                      )}
                      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="md:col-span-2">
                          <label className="text-sm text-gray-600">Expense label (optional)</label>
                          <input
                            value={expenseFormData.description}
                            onChange={(e) => setExpenseFormData((prev) => ({ ...prev, description: e.target.value }))}
                            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            placeholder="e.g. Gutter cleaning service - March 2026"
                          />
                        </div>
                        <div>
                          <label className="text-sm text-gray-600">Expense date</label>
                          <input
                            type="date"
                            value={expenseFormData.expense_date}
                            onChange={(e) =>
                              setExpenseFormData((prev) => ({ ...prev, expense_date: e.target.value }))
                            }
                            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          />
                        </div>
                        <label className="mt-6 flex items-center justify-between gap-4 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700">
                          <span>Billable to homeowner</span>
                          <input
                            type="checkbox"
                            checked={expenseFormData.billable_to_homeowner}
                            onChange={(e) =>
                              setExpenseFormData((prev) => ({
                                ...prev,
                                billable_to_homeowner: e.target.checked,
                              }))
                            }
                          />
                        </label>
                        <label className="flex items-center justify-between gap-4 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700">
                          <span>Needs contractor payment</span>
                          <input
                            type="checkbox"
                            checked={expenseFormData.needs_contractor_payment}
                            onChange={(e) =>
                              setExpenseFormData((prev) => ({
                                ...prev,
                                needs_contractor_payment: e.target.checked,
                              }))
                            }
                          />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label className="text-sm text-gray-600">Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={expenseFormData.amount}
                          onChange={(e) => setExpenseFormData((prev) => ({ ...prev, amount: e.target.value }))}
                          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Internal cost (optional)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={expenseFormData.internal_cost}
                          onChange={(e) =>
                            setExpenseFormData((prev) => ({ ...prev, internal_cost: e.target.value }))
                          }
                          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          placeholder="0.00"
                        />
                        {expenseFormData.amount && expenseFormData.internal_cost && (
                          <div className="mt-1 rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700">
                            Margin: $
                            {(
                              parseFloat(expenseFormData.amount || '0') -
                              parseFloat(expenseFormData.internal_cost || '0')
                            ).toFixed(2)}
                          </div>
                        )}
                      </div>
                      {!isLineItemFlow && (
                        <>
                          <label className="flex items-center justify-between gap-4 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700">
                            <span>Billable to homeowner</span>
                            <input
                              type="checkbox"
                              checked={expenseFormData.billable_to_homeowner}
                              onChange={(e) =>
                                setExpenseFormData((prev) => ({
                                  ...prev,
                                  billable_to_homeowner: e.target.checked,
                                }))
                              }
                            />
                          </label>
                          <label className="flex items-center justify-between gap-4 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700">
                            <span>Needs contractor payment</span>
                            <input
                              type="checkbox"
                              checked={expenseFormData.needs_contractor_payment}
                              onChange={(e) =>
                                setExpenseFormData((prev) => ({
                                  ...prev,
                                  needs_contractor_payment: e.target.checked,
                                }))
                              }
                            />
                          </label>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {expenseWizardStep === 4 && (
                <div className="space-y-4">
                  <div className="rounded-md border border-gray-200 p-4">
                    <p className="text-sm font-medium text-gray-900">Review and visibility</p>
                    {isNewParentFlow ? (
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center justify-between text-sm text-gray-700">
                          <span>Total billed to homeowner</span>
                          <span className="font-semibold">
                            $
                            {(
                              wizardAmountOverride && expenseFormData.amount.trim()
                                ? parseFloat(expenseFormData.amount)
                                : computedWizardLineItemsTotal
                            ).toFixed(2)}
                          </span>
                        </div>
                        {wizardLineItems.map((item, idx) => (
                          <div key={item.tempId} className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2">
                            <div>
                              <p className="text-xs font-medium text-gray-800">
                                {item.description || item.category || 'Line item'}
                              </p>
                              <p className="text-[11px] text-gray-500">${Number(item.amount || 0).toFixed(2)}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setWizardLineItems((prev) =>
                                  prev.map((existing, existingIdx) =>
                                    existingIdx === idx
                                      ? { ...existing, homeowner_visible: !existing.homeowner_visible }
                                      : existing
                                  )
                                )
                              }
                              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                item.homeowner_visible
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-200 text-gray-700'
                              }`}
                            >
                              {item.homeowner_visible ? 'Visible' : 'Hidden'}
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : isLineItemFlow ? (
                      <label className="mt-3 flex items-center justify-between gap-4 text-sm text-gray-700">
                        <span>Show this line item to homeowner</span>
                        <input
                          type="checkbox"
                          checked={expenseFormData.homeowner_visible}
                          onChange={(e) =>
                            setExpenseFormData((prev) => ({
                              ...prev,
                              homeowner_visible: e.target.checked,
                            }))
                          }
                        />
                      </label>
                    ) : (
                      <p className="mt-2 text-sm text-gray-600">
                        Top-level expense visibility is controlled by line items. Add line items to customize homeowner-facing breakdowns.
                      </p>
                    )}
                  </div>
                </div>
              )}
              {error && (
                <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}
            </div>

            <div className="mt-5 flex justify-between gap-2">
              <button
                type="button"
                onClick={() =>
                  expenseWizardStep > 1
                    ? setExpenseWizardStep((prev) => prev - 1)
                    : (() => {
                        setShowExpenseModal(false);
                        resetExpenseForm();
                      })()
                }
                className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700"
              >
                {expenseWizardStep > 1 ? 'Back' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={() => void handleSaveVisitExpenseWizard()}
                disabled={savingExpense || (expenseWizardStep === 2 && !step2Valid) || (expenseWizardStep === 3 && !step3Valid)}
                className="rounded-md bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
              >
                {savingExpense
                  ? 'Saving...'
                  : expenseWizardStep < 4
                    ? 'Continue'
                    : editingExpense
                      ? 'Save Changes'
                      : lineItemParentExpense
                        ? 'Add Line Item'
                        : 'Save Expense'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Task Confirmation Modal */}
      {taskToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-start space-x-4 mb-4">
              <div className="flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Task</h3>
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Are you sure you want to delete this task? This action cannot be undone.
                  </p>
                  {tasks.find(t => t.id === taskToDelete) && (
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                      <div className="text-sm font-medium text-gray-900">
                        {tasks.find(t => t.id === taskToDelete)?.task_title}
                      </div>
                      {tasks.find(t => t.id === taskToDelete)?.task_description && (
                        <div className="text-xs text-gray-600 mt-1">
                          {tasks.find(t => t.id === taskToDelete)?.task_description}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setTaskToDelete(null)}
                disabled={deletingTask}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteTask}
                disabled={deletingTask}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deletingTask ? 'Deleting...' : 'Delete Task'}
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>

      {/* Create Invoice Modal */}
    {showInvoiceModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">
              Create Invoice ({selectedExpenseIdsForInvoice.size} expense{selectedExpenseIdsForInvoice.size === 1 ? '' : 's'})
            </h3>
            <button
              type="button"
              onClick={() => setShowInvoiceModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Selected expense summary */}
          <div className="mb-4 rounded-md bg-gray-50 border border-gray-200 p-3 space-y-1">
            {parentVisitExpenses
              .filter((exp) => selectedExpenseIdsForInvoice.has(exp.id))
              .map((exp) => (
                <div key={exp.id} className="flex justify-between text-sm">
                  <span className="text-gray-700 truncate">
                    {exp.description || exp.category || 'Visit expense'}
                  </span>
                  <span className="ml-3 font-medium text-gray-900 flex-shrink-0">
                    ${Number(exp.amount || 0).toFixed(2)}
                  </span>
                </div>
              ))}
            <div className="flex justify-between text-sm font-semibold border-t border-gray-200 pt-1 mt-1">
              <span className="text-gray-900">Total</span>
              <span className="text-gray-900">
                ${parentVisitExpenses
                  .filter((exp) => selectedExpenseIdsForInvoice.has(exp.id))
                  .reduce((sum, exp) => sum + Number(exp.amount || 0), 0)
                  .toFixed(2)}
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="date"
                value={invoiceDueDate}
                onChange={(e) => setInvoiceDueDate(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-black"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <textarea
                value={invoiceNotes}
                onChange={(e) => setInvoiceNotes(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-black"
                placeholder="Additional notes for the homeowner..."
              />
            </div>
          </div>

          {invoiceError && (
            <div className="mt-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {invoiceError}
            </div>
          )}

          <div className="mt-5 flex gap-3">
            <button
              type="button"
              onClick={() => setShowInvoiceModal(false)}
              disabled={creatingInvoice}
              className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreateInvoice}
              disabled={creatingInvoice}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {creatingInvoice ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <DollarSign className="h-4 w-4" />
              )}
              {creatingInvoice ? 'Creating…' : 'Create Invoice'}
            </button>
          </div>
        </div>
      </div>
    )}
    </AdminLayout>
  );
};

export default VisitDetail;
